/**
 * Iter216 · Vehicle Security Intelligence — Smith SecureVehicle™.
 *
 * Fase 1 delivery:
 *   • Header with brand, Last Updated, disclaimer strip
 *   • 12 KPI tiles
 *   • Search Builder (segments + city/state + include-Flock toggle)
 *   • Leads table + lead detail drawer + Sources modal
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Car, Sparkles, Loader2, ShieldCheck, ShieldAlert, MapPin, DollarSign,
  Users, Building2, Search, Filter, ExternalLink, X, RefreshCw,
  Flame, TrendingUp, Eye, AlertTriangle, Layers, Info, ChevronRight,
  Scale, Map as MapIcon, Radio, ChevronDown, ChevronUp,
  Brain, Target, Zap, MessageSquare, Copy, Wand2,
  Printer, Mail, MessageCircle, Send,
  Download, Link as LinkIcon, Link2Off, Unlink,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import ExportButton from "../../components/ExportButton";
import MultiSelectCombo from "../../components/MultiSelectCombo";
import ClairePitchModal from "../../components/ClairePitchModal";
import CityPicker from "../../components/CityPicker";
import VehicleSecurityMap, { applyVehicleSecurityFilters, resolveLeadCityPosition } from "../../components/smith/VehicleSecurityMap";
import { useLang } from "../../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLE = {
  critical:  { color: "#EF4444", bg: "rgba(239,68,68,0.14)",  icon: "🔥", label: "Critical" },
  very_high: { color: "#F97316", bg: "rgba(249,115,22,0.14)", icon: "🟠", label: "Very High" },
  high:      { color: "#EAB308", bg: "rgba(234,179,8,0.14)",  icon: "🟡", label: "High" },
  medium:    { color: "#0EA5E9", bg: "rgba(14,165,233,0.14)", icon: "🔵", label: "Medium" },
  low:       { color: "#94A3B8", bg: "rgba(148,163,184,0.14)",icon: "⚪", label: "Low" },
};

const KPI_TILES = [
  { key: "total_leads",                       i18n: "vs.kpi.total_leads",       icon: Users,     color: "#8B5CF6" },
  { key: "critical_opportunity_leads",        i18n: "vs.kpi.critical",          icon: Flame,     color: "#EF4444" },
  { key: "high_opportunity_leads",            i18n: "vs.kpi.high_opportunity",  icon: TrendingUp,color: "#F97316" },
  { key: "properties_with_existing_cctv",     i18n: "vs.kpi.existing_cctv",     icon: Eye,       color: "#0EA5E9" },
  { key: "properties_with_existing_alpr",     i18n: "vs.kpi.existing_alpr",     icon: Car,       color: "#EAB308" },
  { key: "properties_with_guard_house",       i18n: "vs.kpi.guard_house",       icon: ShieldCheck,color:"#10B981" },
  { key: "properties_with_security_officers", i18n: "vs.kpi.security_officers", icon: ShieldAlert,color:"#F59E0B" },
  { key: "properties_with_multiple_entrances",i18n: "vs.kpi.multi_entrance",    icon: Layers,    color: "#0EA5E9" },
  { key: "properties_with_large_parking_lot", i18n: "vs.kpi.large_parking",     icon: MapPin,    color: "#22C55E" },
  { key: "vehicle_theft_risk_leads",          i18n: "vs.kpi.theft_risk",        icon: AlertTriangle, color:"#DC2626" },
  { key: "replacement_alpr_opportunities",    i18n: "vs.kpi.replacement_alpr",  icon: RefreshCw, color: "#A78BFA" },
  { key: "cross_sell_opportunities",          i18n: "vs.kpi.cross_sell",        icon: Sparkles,  color: "#EC4899" },
];

// Iter342 — Predicates that drive click-through KPI filtering. Each
// predicate is applied to a lead row and returns `true` if the lead
// should remain visible when the matching tile is active.
const _sig = (L) => (L?.signals || []).map((s) => String(s).toLowerCase());
const _hasSig = (L, keywords) => {
  const sig = _sig(L);
  return sig.some((s) => keywords.some((k) => s.includes(k)));
};
export const KPI_PREDICATES = {
  total_leads:                        () => true,
  critical_opportunity_leads:         (L) => (L.score || 0) >= 90,
  high_opportunity_leads:             (L) => (L.score || 0) >= 80,
  properties_with_existing_cctv:      (L) => _hasSig(L, ["cctv", "camera"]),
  properties_with_existing_alpr:      (L) => L?.flock_detected === true || _hasSig(L, ["alpr", "flock", "license plate"]),
  properties_with_guard_house:        (L) => _hasSig(L, ["guard_house", "guard house", "guardhouse", "guard"]),
  properties_with_security_officers:  (L) => _hasSig(L, ["security_officer", "security officer", "officer"]),
  properties_with_multiple_entrances: (L) => _hasSig(L, ["multi_entrance", "multiple_entrance", "multiple entrance", "entrance"]),
  properties_with_large_parking_lot:  (L) => _hasSig(L, ["large_parking", "large parking", "parking_lot", "parking lot", "parking"]),
  vehicle_theft_risk_leads:           (L) => _hasSig(L, ["theft", "vehicle_theft", "auto theft"]),
  replacement_alpr_opportunities:     (L) => L?.replacement_alpr_target === true || _hasSig(L, ["replacement_alpr", "replacement alpr", "obsolete"]),
  cross_sell_opportunities:           (L) => L?.cross_sell === true || _hasSig(L, ["cross_sell", "cross-sell", "cross sell"]),
};

export default function SmithVehicleSecurity() {
  const { t, lang } = useLang();
  // Iter286 · URL query sync — read initial segments/city/state from
  // `?segments=warehouse,hospital&city=Orlando&state=FL` so managers
  // can share a pre-filtered discovery view with reps by copying the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const _urlSegments = (searchParams.get("segments") || "")
    .split(",").map((s) => s.trim()).filter(Boolean);
  const _urlCity  = searchParams.get("city")  || "Orlando";
  const _urlState = searchParams.get("state") || "FL";
  const [dashboard, setDashboard] = useState(null);
  const [segments, setSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState(
    _urlSegments.length ? _urlSegments : ["hoa", "apartment_community", "warehouse"]);
  const [city, setCity] = useState(_urlCity);
  const [state, setState] = useState(_urlState);
  const [includeOpenai, setIncludeOpenai] = useState(true);
  const [includeCrm, setIncludeCrm] = useState(true);
  const [searching, setSearching] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [tierFilter, setTierFilter] = useState("");
  const [flockFilter, setFlockFilter] = useState("all"); // all|only|exclude
  const [kpiFilter, setKpiFilter] = useState(null);       // Iter342 · active KPI tile key
  // Iter344 · B3 — Strategic filters
  const [enrichFilter, setEnrichFilter] = useState("all"); // all|email|phone|website
  const [sizeFilter, setSizeFilter] = useState("all");     // all|small|mid|large
  const [tempFilter, setTempFilter] = useState("all");     // all|hot|warm|cold
  // Iter344 · B5 — Row selection + pitch modal
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [pitchLeads, setPitchLeads] = useState([]); // non-empty => modal open
  const leadsSectionRef = useRef(null);                    // Iter342 · smooth-scroll target
  const [selectedLead, setSelectedLead] = useState(null);
  const [showSources, setShowSources] = useState(false);
  const [disclaimer, setDisclaimer] = useState("");
  // Iter218 — Regulatory + Geo-Map
  const [regAlert, setRegAlert] = useState(null);
  const [showRegModal, setShowRegModal] = useState(false);
  const [regulations, setRegulations] = useState([]);
  const [geoMap, setGeoMap] = useState(null);
  const [mapOpen, setMapOpen] = useState(true);
  // Iter309 · Real Google Maps — layout switch + privacy + hover-sync
  const [mapLayout, setMapLayout] = useState(
    () => (typeof window !== "undefined" && window.localStorage
            ? window.localStorage.getItem("vs-map-layout") : null) || "clusters");
  const [mapPrivacy, setMapPrivacy] = useState("city"); // "city" | "exact"
  const [hoveredLeadId, setHoveredLeadId] = useState(null);
  // Iter311 · lifted state so the leads table can filter by the same
  // radius / route the map is using.
  const [mapRadiusCenter, setMapRadiusCenter] = useState(null);
  const [mapRadiusMiles, setMapRadiusMiles] = useState(0);
  const [mapRouteOrder, setMapRouteOrder] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const isMaster = userRole === "super_admin" || userRole === "admin"
                     || userRole === "master_demo";
  useEffect(() => {
    try { window.localStorage?.setItem("vs-map-layout", mapLayout); }
    catch { /* ignore */ }
  }, [mapLayout]);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, authHeader);
        setUserRole(r.data?.role || r.data?.user?.role || null);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line
  }, []);
  // Iter223 — Google Ads Keywords modal
  const [showKwModal, setShowKwModal] = useState(false);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }),
    []);

  useEffect(() => {
    (async () => {
      try {
        const [d, s, ra, gm] = await Promise.all([
          axios.get(`${API}/vehicle-security/dashboard`, authHeader),
          axios.get(`${API}/vehicle-security/segments`, authHeader),
          axios.get(`${API}/vehicle-security/regulatory-alert`, authHeader),
          axios.get(`${API}/vehicle-security/geo-map`, authHeader),
        ]);
        setDashboard(d.data);
        setDisclaimer(d.data?.disclaimer || "");
        setSegments(s.data?.segments || []);
        setRegAlert(ra.data);
        setGeoMap(gm.data);
      } catch { /* silent */ }
      loadLeads();
    })();
    // eslint-disable-next-line
  }, []);

  const openRegModal = async () => {
    setShowRegModal(true);
    if (regulations.length === 0) {
      try {
        const r = await axios.get(`${API}/vehicle-security/regulations?state=FL`, authHeader);
        setRegulations(r.data?.regulations || []);
      } catch { /* silent */ }
    }
  };

  const refreshGeoMap = async () => {
    try {
      const r = await axios.get(`${API}/vehicle-security/geo-map`, authHeader);
      setGeoMap(r.data);
    } catch { /* silent */ }
  };

  const loadLeads = async () => {
    setLoadingLeads(true);
    try {
      const params = new URLSearchParams({ sort: "score_desc", limit: "50" });
      if (tierFilter) params.set("tier", tierFilter);
      if (flockFilter === "only") params.set("flock_detected", "true");
      if (flockFilter === "exclude") params.set("flock_detected", "false");
      if (selectedSegments.length) params.set("segments", selectedSegments.join(","));
      const r = await axios.get(
        `${API}/vehicle-security/leads?${params.toString()}`,
        authHeader);
      setLeads(r.data?.leads || []);
    } catch (ex) {
      toast.error("Falha ao carregar leads");
    } finally { setLoadingLeads(false); }
  };

  useEffect(() => { loadLeads(); /* eslint-disable-next-line */ }, [tierFilter, flockFilter, selectedSegments]);

  // Iter286 · Keep URL in sync with the chip selection + city/state so the
  // link is always shareable. Uses `replace` (no history entry per click).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedSegments.length) next.set("segments", selectedSegments.join(","));
    else next.delete("segments");
    if (city  && city.trim())  next.set("city",  city.trim());
    else next.delete("city");
    if (state && state.trim()) next.set("state", state.trim().toUpperCase());
    else next.delete("state");
    // Avoid pushing a new entry on every render if nothing changed.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSegments, city, state]);

  const runSearch = async () => {
    if (!selectedSegments.length) {
      toast.error("Escolha ao menos 1 segmento");
      return;
    }
    setSearching(true);
    try {
      const r = await axios.post(`${API}/vehicle-security/search`,
        { segments: selectedSegments, city, state,
          include_openai: includeOpenai, include_crm: includeCrm },
        { ...authHeader, timeout: 240000 });
      const total = r.data?.total_new_or_updated || 0;
      toast.success(
        `Discovery pronto · ${r.data?.openai?.count || 0} via OpenAI + ${r.data?.crm?.count || 0} via CRM`);
      if (r.data?.openai?.errors?.length) {
        toast.warning(`Erros: ${r.data.openai.errors.slice(0,2).join("; ")}`);
      }
      // Refresh views (scoped to just-searched segments — Iter284)
      const segCsv = encodeURIComponent(selectedSegments.join(","));
      const [d, l, gm] = await Promise.all([
        axios.get(`${API}/vehicle-security/dashboard`, authHeader),
        axios.get(`${API}/vehicle-security/leads?sort=score_desc&limit=50&segments=${segCsv}`, authHeader),
        axios.get(`${API}/vehicle-security/geo-map`, authHeader),
      ]);
      setDashboard(d.data); setLeads(l.data?.leads || []); setGeoMap(gm.data);
      if (total === 0) toast.info("Nenhum lead novo — amplie segmento ou cidade");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha na discovery");
    } finally { setSearching(false); }
  };

  const toggleSegment = (id) => {
    setSelectedSegments((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  // Iter311 · Table syncs with map filters + radius. Ordered by route
  // when the user hit "Roteirizar", else keeps score_desc from the API.
  // Iter342 · Also honors the click-through KPI filter when active.
  // Iter344 · B3 — Also filters by enrichment status, size, and AI score temp.
  const visibleLeads = useMemo(() => {
    const base = applyVehicleSecurityFilters(leads, {
      filters: { tier: tierFilter, flock: flockFilter },
      radiusCenter: mapRadiusCenter,
      radiusMilesValue: mapRadiusMiles,
      positionsResolver: resolveLeadCityPosition,
    });
    const kpiPred = kpiFilter && KPI_PREDICATES[kpiFilter];
    let filtered = kpiPred ? base.filter(kpiPred) : base;

    // Enrichment · row must have the requested contact channel present.
    if (enrichFilter !== "all") {
      filtered = filtered.filter((L) => {
        if (enrichFilter === "email")   return !!(L.email || L.contact_email);
        if (enrichFilter === "phone")   return !!(L.phone || L.contact_phone);
        if (enrichFilter === "website") return !!(L.website || L.url);
        return true;
      });
    }
    // Size · uses employees / sqft when available, or a semantic bucket.
    if (sizeFilter !== "all") {
      filtered = filtered.filter((L) => {
        const e = Number(L.employees || L.employees_estimate || 0);
        const sqft = Number(L.sqft || L.sqft_building || 0);
        const bucket = L.size_bucket || L.company_size;
        if (sizeFilter === "small")  return (e > 0 && e < 50)   || (sqft > 0 && sqft < 10000)   || bucket === "small";
        if (sizeFilter === "mid")    return (e >= 50 && e < 250)|| (sqft >= 10000 && sqft < 50000)|| bucket === "mid";
        if (sizeFilter === "large")  return (e >= 250)          || (sqft >= 50000)              || bucket === "large";
        return true;
      });
    }
    // Temperature · AI score bands (hot ≥ 80, warm 50-79, cold < 50).
    if (tempFilter !== "all") {
      filtered = filtered.filter((L) => {
        const s = Number(L.score || 0);
        if (tempFilter === "hot")  return s >= 80;
        if (tempFilter === "warm") return s >= 50 && s < 80;
        if (tempFilter === "cold") return s < 50;
        return true;
      });
    }

    if (mapRouteOrder && mapRouteOrder.length > 0) {
      const index = new Map(mapRouteOrder.map((id, i) => [id, i]));
      return [...filtered].sort(
        (a, b) => (index.get(a.id) ?? 9999) - (index.get(b.id) ?? 9999));
    }
    return filtered;
  }, [leads, tierFilter, flockFilter, kpiFilter, enrichFilter, sizeFilter,
      tempFilter, mapRadiusCenter, mapRadiusMiles, mapRouteOrder]);

  return (
    <div className="min-h-screen" style={{ background: "#0B1120", color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="vs-header"
          title={t("vs.header.title")}
          subtitle={t("vs.header.subtitle")}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)", color: "white" }}>
              <Car className="w-5 h-5" />
            </div>
          }
        />

        <main className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 space-y-5"
              data-testid="vs-main">

          {/* Regulatory disclaimer (§ 1) */}
          <div className="flex items-start gap-2">
            <div data-testid="vs-disclaimer"
              className="flex-1 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-[11.5px] text-amber-200 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>{disclaimer || t("vs.disclaimer_loading")}</div>
            </div>
            {/* Iter223 — Google Ads Keywords launcher */}
            <button type="button" onClick={() => setShowKwModal(true)}
              data-testid="vs-keywords-btn"
              title={t("vs.google_ads_title")}
              className="shrink-0 h-[52px] px-3 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center gap-1.5 border border-sky-500/40 hover:border-sky-400 transition-colors"
              style={{ background: "linear-gradient(135deg, rgba(14,165,233,0.20), rgba(3,105,161,0.10))" }}>
              <Wand2 className="w-3.5 h-3.5 text-sky-300" />
              <div className="text-left leading-tight">
                <div>{t("vs.google_ads")}</div>
                <div className="text-[9.5px] text-slate-400 font-normal">{t("vs.google_ads_sub")}</div>
              </div>
            </button>
          </div>

          {/* Iter218 — Regulatory Watch alert bar (§ 26-27) */}
          {regAlert && (
            <button type="button" onClick={openRegModal}
              data-testid="vs-regulatory-alert-bar"
              className="w-full text-left rounded-xl border p-3 flex items-center gap-2 transition-colors"
              style={{
                borderColor:
                  regAlert.status === "HIGH_ATTENTION" ? "rgba(239,68,68,0.35)" :
                  regAlert.status === "MONITOR" ? "rgba(245,158,11,0.35)" :
                  "rgba(16,185,129,0.35)",
                background:
                  regAlert.status === "HIGH_ATTENTION" ? "rgba(239,68,68,0.06)" :
                  regAlert.status === "MONITOR" ? "rgba(245,158,11,0.06)" :
                  "rgba(16,185,129,0.06)",
              }}>
              <Scale className={`w-4 h-4 shrink-0 ${
                regAlert.status === "HIGH_ATTENTION" ? "text-red-300" :
                regAlert.status === "MONITOR" ? "text-amber-300" : "text-emerald-300"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9.5px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                        style={{
                          background:
                            regAlert.status === "HIGH_ATTENTION" ? "rgba(239,68,68,0.2)" :
                            regAlert.status === "MONITOR" ? "rgba(245,158,11,0.2)" :
                            "rgba(16,185,129,0.2)",
                          color:
                            regAlert.status === "HIGH_ATTENTION" ? "#FCA5A5" :
                            regAlert.status === "MONITOR" ? "#FCD34D" : "#6EE7B7",
                        }}>
                    Florida · {regAlert.status?.replace("_", " ")}
                  </span>
                  <span className="text-[11px] font-bold text-white">{t("vs.reg_watch")}</span>
                  <span className="text-[10.5px] text-slate-400">
                    · {t("vs.reg_items_tracked", { n: regAlert.regulations_count })}
                  </span>
                </div>
                <div className="text-[11.5px] text-slate-200 mt-0.5 line-clamp-2">
                  {regAlert.message}
                </div>
              </div>
              <div className="text-[10.5px] text-slate-400 shrink-0 inline-flex items-center gap-1">
                {t("vs.reg_open")} <ChevronRight className="w-3 h-3" />
              </div>
            </button>
          )}

          {/* KPI grid — Iter342 · Click-through filters. Tapping a tile
              scopes the leads table to matching rows and scrolls to it.
              Tapping the same tile again (or "Total Leads") clears. */}
          <section data-testid="vs-kpis" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
            {KPI_TILES.map((tile) => {
              const Icon = tile.icon;
              const v = dashboard?.[tile.key] ?? 0;
              const active = kpiFilter === tile.key;
              const clickable = v > 0 || tile.key === "total_leads";
              const handleClick = () => {
                if (!clickable) return;
                // "Total Leads" always clears — it means "no KPI filter".
                const next = (tile.key === "total_leads" || active) ? null : tile.key;
                setKpiFilter(next);
                if (next && leadsSectionRef.current) {
                  leadsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              };
              return (
                <button key={tile.key}
                     type="button"
                     onClick={handleClick}
                     disabled={!clickable}
                     data-testid={`vs-kpi-${tile.key}`}
                     aria-pressed={active}
                     title={clickable
                       ? (active ? t("vs.kpi.clear_filter") : t("vs.kpi.filter_by", { label: t(tile.i18n) }))
                       : t("vs.kpi.no_matches")}
                     className={`text-left rounded-xl border-2 p-3 transition-all ${
                       active
                         ? "shadow-lg -translate-y-[1px]"
                         : clickable
                           ? "border-white/10 hover:border-white/25 hover:-translate-y-[1px]"
                           : "border-white/10 opacity-60 cursor-not-allowed"
                     }`}
                     style={active ? {
                       borderColor: tile.color,
                       background: `${tile.color}18`,
                       boxShadow: `0 0 0 3px ${tile.color}33, 0 4px 12px ${tile.color}22`,
                     } : undefined}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                         style={{ background: `${tile.color}22`, color: tile.color }}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    {active && (
                      <span className="text-[8.5px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
                            style={{ background: tile.color, color: "white" }}
                            data-testid={`vs-kpi-${tile.key}-active-badge`}>
                        {t("vs.kpi.active")}
                      </span>
                    )}
                  </div>
                  <div className="text-[20px] font-extrabold tabular-nums text-white">{v}</div>
                  <div className="text-[10.5px] text-slate-400 leading-tight">{t(tile.i18n)}</div>
                </button>
              );
            })}
          </section>

          {/* Pipeline headline */}
          {dashboard?.estimated_pipeline_value?.high > 0 && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-300" />
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-emerald-300 font-bold">
                    {t("vs.pipeline_label")}
                  </div>
                  <div className="text-[14px] font-extrabold text-white"
                       data-testid="vs-pipeline-headline">
                    {dashboard.estimated_pipeline_value.headline}
                  </div>
                </div>
              </div>
              <div className="text-[10.5px] text-slate-400">
                {t("vs.pipeline_footer")}
              </div>
            </div>
          )}

          {/* Iter218 — Central Florida Geo Map (§ 24) */}
          {geoMap && (
            <section data-testid="vs-geo-map-section"
              className="rounded-2xl border border-white/10 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <MapIcon className="w-3.5 h-3.5 text-sky-300" />
                <h3 className="text-[11px] uppercase tracking-widest text-slate-300 font-bold">
                  {t("vs.geo.title")}
                </h3>
                <div className="text-[10.5px] text-slate-400 ml-2">
                  {t("vs.geo.summary", { cities: geoMap.cities_covered, leads: geoMap.leads_covered })}
                  {geoMap.critical_total > 0 && (
                    <span className="ml-1 text-red-300">
                      {t("vs.geo.critical_extra", { n: geoMap.critical_total })}
                    </span>
                  )}
                </div>
                <button type="button" onClick={refreshGeoMap}
                  data-testid="vs-geo-map-refresh"
                  title={t("vs.geo.refresh")}
                  className="ml-auto w-7 h-7 rounded border border-white/15 flex items-center justify-center text-slate-400 hover:text-white">
                  <RefreshCw className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => setMapOpen((v) => !v)}
                  data-testid="vs-geo-map-toggle"
                  className="w-7 h-7 rounded border border-white/15 flex items-center justify-center text-slate-400 hover:text-white">
                  {mapOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>

              {mapOpen && (
                <>
                  <VehicleSecurityMap
                    leads={leads}
                    cities={geoMap.cities || []}
                    layout={mapLayout}
                    onLayoutChange={setMapLayout}
                    privacyMode={mapPrivacy}
                    onPrivacyChange={setMapPrivacy}
                    isMaster={isMaster}
                    onLeadSelect={(L) => setSelectedLead(L)}
                    focusedLeadId={hoveredLeadId}
                    filters={{ tier: tierFilter, flock: flockFilter }}
                    onFiltersChange={(next) => {
                      if (next.tier !== undefined) setTierFilter(next.tier || "");
                      if (next.flock !== undefined) setFlockFilter(next.flock || "all");
                    }}
                    radiusCenter={mapRadiusCenter}
                    onRadiusCenterChange={setMapRadiusCenter}
                    radiusMiles={mapRadiusMiles}
                    onRadiusMilesChange={setMapRadiusMiles}
                    routeOrder={mapRouteOrder}
                    onRouteChange={setMapRouteOrder}
                    height={mapLayout === "split" ? 520 : 420} />
                  <div className="text-[10.5px] text-slate-500 italic">
                    {mapPrivacy === "exact"
                      ? t("vsm.exact_mode_hint")
                      : t("vsm.city_mode_hint")}
                  </div>
                </>
              )}
            </section>
          )}

          {/* Discovery builder */}
          <section data-testid="vs-search-builder"
            className="rounded-2xl border border-white/10 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Search className="w-3.5 h-3.5 text-purple-300" />
              <h3 className="text-[11px] uppercase tracking-widest text-slate-300 font-bold">
                {t("vs.discovery.title")}
              </h3>
              <span className="ml-auto text-[10.5px] text-slate-500">
                {t("vs.discovery.subtitle")}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <label className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold block mb-1">{t("vs.discovery.city")}</label>
                <CityPicker
                  value={city}
                  onChange={setCity}
                  fetchUrl={`${API}/vehicle-security/city-stats${state ? `?state=${state.toUpperCase()}` : ""}`}
                  authHeader={authHeader}
                  testidPrefix="vs-city-picker"
                  placeholder="Selecione ou digite uma cidade FL"
                  fallbackCities={[
                    "Miami", "Jacksonville", "Tampa", "Orlando", "St. Petersburg",
                    "Hialeah", "Port St. Lucie", "Cape Coral", "Tallahassee",
                    "Fort Lauderdale", "Pembroke Pines", "Hollywood", "Miramar",
                    "Gainesville", "Coral Springs", "Miami Gardens", "Clearwater",
                    "Palm Bay", "West Palm Beach", "Lakeland", "Pompano Beach",
                    "Davie", "Miami Beach", "Sunrise", "Boca Raton", "Deltona",
                    "Plantation", "Fort Myers", "Sarasota", "Palm Coast",
                    "Deerfield Beach", "Melbourne", "Boynton Beach", "Lauderhill",
                    "Weston", "Kissimmee", "Homestead", "Delray Beach", "Tamarac",
                    "Daytona Beach", "North Miami", "Wellington", "Jupiter",
                    "North Port", "Coconut Creek", "Ocala", "Bradenton",
                    "Sanford", "Margate", "Doral", "Winter Garden", "Winter Park",
                    "Ocoee", "Apopka", "Altamonte Springs", "Oviedo", "Casselberry",
                    "Kendall", "Aventura", "Coral Gables", "Naples", "Marco Island",
                    "Key West", "Panama City", "Pensacola", "Punta Gorda",
                    "Vero Beach", "Stuart",
                  ]}
                />
              </div>
              <div>
                <label className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold block mb-1">{t("vs.discovery.state")}</label>
                <input value={state} onChange={(e) => setState(e.target.value.toUpperCase().slice(0,2))}
                  data-testid="vs-input-state"
                  className="w-full h-9 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
              </div>
              <div className="flex items-end gap-1.5">
                <label className="text-[11px] text-slate-300 inline-flex items-center gap-1.5 cursor-pointer"
                       data-testid="vs-toggle-openai">
                  <input type="checkbox" checked={includeOpenai}
                    onChange={(e) => setIncludeOpenai(e.target.checked)} />
                  {t("vs.discovery.openai")}
                </label>
                <label className="text-[11px] text-slate-300 inline-flex items-center gap-1.5 cursor-pointer"
                       data-testid="vs-toggle-crm">
                  <input type="checkbox" checked={includeCrm}
                    onChange={(e) => setIncludeCrm(e.target.checked)} />
                  {t("vs.discovery.crm")}
                </label>
              </div>
              <div className="flex items-end">
                <button type="button" onClick={runSearch} disabled={searching}
                  data-testid="vs-run-search-btn"
                  className="w-full h-9 rounded-lg text-[12px] font-bold text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                  {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  {searching ? t("vs.discovery.running") : t("vs.discovery.run")}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">
                  {t("vs.discovery.segments_hint")}
                </div>
                <button type="button"
                  data-testid="vs-copy-share-link"
                  onClick={() => {
                    const url = new URL(window.location.href);
                    if (selectedSegments.length) url.searchParams.set("segments", selectedSegments.join(","));
                    if (city)  url.searchParams.set("city", city);
                    if (state) url.searchParams.set("state", state.toUpperCase());
                    navigator.clipboard?.writeText(url.toString())
                      .then(() => toast.success(t("vs.discovery.link_copied")))
                      .catch(() => toast.error(t("vs.discovery.link_copy_failed")));
                  }}
                  title={t("vs.discovery.copy_link_title")}
                  className="h-6 px-2 rounded text-[9.5px] font-bold text-indigo-200 hover:text-white inline-flex items-center gap-1 border border-indigo-400/40 bg-indigo-500/10 hover:bg-indigo-500/25">
                  <Copy className="w-2.5 h-2.5" /> {t("vs.discovery.copy_link")}
                </button>
              </div>
              {/* Iter344 · B1 — Searchable dropdown w/ Select All replaces the
                  legacy chip strip. Legacy chips kept as a compact preview
                  UNDER the dropdown so tier-1 segments stay visible. */}
              <div data-testid="vs-segment-picker" className="max-w-md">
                <MultiSelectCombo
                  testid="vs-segments-combo"
                  options={segments.map((s) => ({
                    label: `${s.emoji || "•"}  ${s.label}${s.tier === 1 ? "  · T1" : ""}`,
                  }))}
                  values={segments
                    .filter((s) => selectedSegments.includes(s.id))
                    .map((s) => `${s.emoji || "•"}  ${s.label}${s.tier === 1 ? "  · T1" : ""}`)}
                  onChange={(nextLabels) => {
                    const nextIds = segments
                      .filter((s) => nextLabels.includes(`${s.emoji || "•"}  ${s.label}${s.tier === 1 ? "  · T1" : ""}`))
                      .map((s) => s.id);
                    setSelectedSegments(nextIds);
                  }}
                  placeholder={t("vs.discovery.segments_placeholder") || "Selecionar setores…"}
                  isPt={lang === "pt"}
                />
              </div>
              {/* Compact preview chips (read-only quick reference). */}
              {selectedSegments.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-0.5" data-testid="vs-segment-chips">
                  {segments
                    .filter((s) => selectedSegments.includes(s.id))
                    .map((s) => (
                      <span key={s.id}
                            data-testid={`vs-segment-${s.id}`}
                            className="text-[10.5px] font-semibold px-2 h-6 rounded-full border border-red-500/60 bg-red-500/15 text-red-800 dark:text-red-100 inline-flex items-center gap-1"
                            title={`Tier ${s.tier}`}>
                        <span className="text-[12px]">{s.emoji}</span>
                        <span>{s.label}</span>
                      </span>
                    ))}
                </div>
              )}
            </div>
          </section>

          {/* Leads table */}
          <section ref={leadsSectionRef} data-testid="vs-leads-section">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-3.5 h-3.5 text-slate-400" />
                <h3 className="text-[11px] uppercase tracking-widest text-slate-300 font-bold">
                  {t("vs.leads.section_title")}
                  <span className="ml-2 text-slate-500 font-normal" data-testid="vs-leads-count">
                    {t("vs.leads.count", { shown: visibleLeads.length, total: leads.length })}
                    {selectedSegments.length > 0 && (
                      <span className="ml-1 text-indigo-300/80 normal-case tracking-normal">
                        {t("vs.leads.filter_prefix", { seg: selectedSegments.map((s) => s.replace(/_/g, " ")).join(", ") })}
                      </span>
                    )}
                    {mapRadiusCenter && mapRadiusMiles > 0 && (
                      <span className="ml-1 text-[#C9A24E] normal-case tracking-normal">
                        {t("vs.leads.radius_label", { miles: mapRadiusMiles })}
                      </span>
                    )}
                    {mapRouteOrder && mapRouteOrder.length > 0 && (
                      <span className="ml-1 text-sky-300 normal-case tracking-normal">
                        {t("vs.leads.route_label")}
                      </span>
                    )}
                  </span>
                </h3>
                {kpiFilter && (
                  <button type="button" onClick={() => setKpiFilter(null)}
                          data-testid="vs-kpi-filter-clear"
                          className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10.5px] font-bold border transition-colors"
                          style={{
                            borderColor: (KPI_TILES.find((k) => k.key === kpiFilter)?.color || "#8B5CF6") + "70",
                            background: (KPI_TILES.find((k) => k.key === kpiFilter)?.color || "#8B5CF6") + "18",
                            color: KPI_TILES.find((k) => k.key === kpiFilter)?.color || "#8B5CF6",
                          }}
                          title={t("vs.kpi.clear_filter")}>
                    {t(KPI_TILES.find((k) => k.key === kpiFilter)?.i18n || "vs.kpi.total_leads")}
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <ExportButton
                  source="vehicle-security"
                  testidPrefix="vs-export"
                  filters={{
                    segments: selectedSegments,
                    city, state,
                    tier: tierFilter,
                    flock_detected: flockFilter === "only" ? "true"
                                  : flockFilter === "exclude" ? "false" : "",
                  }}
                />
                <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}
                  data-testid="vs-filter-tier"
                  className="h-7 px-2 rounded border border-white/15 bg-black/30 text-[11px] text-white">
                  <option value="">{t("vs.filter.tier_all")}</option>
                  <option value="critical">{t("vs.filter.tier_critical")}</option>
                  <option value="very_high">{t("vs.filter.tier_very_high")}</option>
                  <option value="high">{t("vs.filter.tier_high")}</option>
                  <option value="medium">{t("vs.filter.tier_medium")}</option>
                  <option value="low">{t("vs.filter.tier_low")}</option>
                </select>
                <select value={flockFilter} onChange={(e) => setFlockFilter(e.target.value)}
                  data-testid="vs-filter-flock"
                  className="h-7 px-2 rounded border border-white/15 bg-black/30 text-[11px] text-white">
                  <option value="all">{t("vs.filter.flock_all")}</option>
                  <option value="only">{t("vs.filter.flock_only")}</option>
                  <option value="exclude">{t("vs.filter.flock_exclude")}</option>
                </select>
                {/* Iter344 · B3 — Strategic filters */}
                <select value={enrichFilter} onChange={(e) => setEnrichFilter(e.target.value)}
                  data-testid="vs-filter-enrichment"
                  title={t("vs.filter.enrich_title") || "Filtrar por enriquecimento"}
                  className="h-7 px-2 rounded border border-white/15 bg-black/30 text-[11px] text-white">
                  <option value="all">{t("vs.filter.enrich_all") || "Enriq: todos"}</option>
                  <option value="email">{t("vs.filter.enrich_email") || "Com email"}</option>
                  <option value="phone">{t("vs.filter.enrich_phone") || "Com telefone"}</option>
                  <option value="website">{t("vs.filter.enrich_website") || "Com site"}</option>
                </select>
                <select value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)}
                  data-testid="vs-filter-size"
                  title={t("vs.filter.size_title") || "Filtrar por porte"}
                  className="h-7 px-2 rounded border border-white/15 bg-black/30 text-[11px] text-white">
                  <option value="all">{t("vs.filter.size_all") || "Porte: todos"}</option>
                  <option value="small">{t("vs.filter.size_small") || "Pequeno (<50)"}</option>
                  <option value="mid">{t("vs.filter.size_mid") || "Médio (50-250)"}</option>
                  <option value="large">{t("vs.filter.size_large") || "Grande (250+)"}</option>
                </select>
                <select value={tempFilter} onChange={(e) => setTempFilter(e.target.value)}
                  data-testid="vs-filter-temp"
                  title={t("vs.filter.temp_title") || "Filtrar por score IA"}
                  className="h-7 px-2 rounded border border-white/15 bg-black/30 text-[11px] text-white">
                  <option value="all">{t("vs.filter.temp_all") || "Score: todos"}</option>
                  <option value="hot">{t("vs.filter.temp_hot") || "🔥 Quente (80+)"}</option>
                  <option value="warm">{t("vs.filter.temp_warm") || "🟡 Morno (50-79)"}</option>
                  <option value="cold">{t("vs.filter.temp_cold") || "🧊 Frio (<50)"}</option>
                </select>
                {selectedRows.size > 0 && (
                  <button type="button"
                    onClick={() => {
                      const chosen = visibleLeads.filter((L) => selectedRows.has(L.id));
                      setPitchLeads(chosen);
                    }}
                    data-testid="vs-batch-pitch-btn"
                    className="h-7 px-2.5 rounded text-[11px] font-bold text-white inline-flex items-center gap-1.5"
                    style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
                    <Sparkles className="w-3 h-3" />
                    Enviar em lote ({selectedRows.size})
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]" data-testid="vs-leads-table">
                  <thead className="bg-white/5 text-slate-400 uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="text-left px-2 py-2 font-bold w-8">
                        <input type="checkbox"
                          data-testid="vs-select-all"
                          checked={visibleLeads.length > 0 && visibleLeads.every((L) => selectedRows.has(L.id))}
                          onChange={(e) => {
                            const next = new Set(selectedRows);
                            if (e.target.checked) visibleLeads.forEach((L) => next.add(L.id));
                            else visibleLeads.forEach((L) => next.delete(L.id));
                            setSelectedRows(next);
                          }}
                          title="Selecionar/desmarcar todos" />
                      </th>
                      <th className="text-left px-3 py-2 font-bold">{t("vs.tbl.property")}</th>
                      <th className="text-left px-2 py-2 font-bold">{t("vs.tbl.segment")}</th>
                      <th className="text-left px-2 py-2 font-bold">{t("vs.tbl.score")}</th>
                      <th className="text-left px-2 py-2 font-bold">{t("vs.tbl.signals")}</th>
                      <th className="text-left px-2 py-2 font-bold">{t("vs.tbl.opportunity")}</th>
                      <th className="text-left px-2 py-2 font-bold">{t("vs.tbl.status")}</th>
                      <th className="text-right px-3 py-2 font-bold">{t("vs.tbl.actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {loadingLeads && (
                      <tr><td colSpan={8} className="p-6 text-center text-slate-500">
                        <Loader2 className="w-4 h-4 inline-block animate-spin mr-1" /> {t("vs.tbl.loading")}
                      </td></tr>
                    )}
                    {!loadingLeads && visibleLeads.length === 0 && (
                      <tr><td colSpan={8} className="p-6 text-center text-slate-500">
                        {leads.length === 0
                          ? t("vs.tbl.empty_run_discovery")
                          : t("vs.tbl.empty_filters")}
                      </td></tr>
                    )}
                    {visibleLeads.map((L, idx) => {
                      const tstyle = TIER_STYLE[L.tier] || TIER_STYLE.low;
                      const sigs = L.signals || [];
                      const stopN = mapRouteOrder?.indexOf(L.id) ?? -1;
                      return (
                        <tr key={L.id} data-testid={`vs-lead-row-${L.id}`}
                            className={`hover:bg-white/5 cursor-pointer ${hoveredLeadId === L.id ? "bg-white/5" : ""}`}
                            onClick={() => setSelectedLead(L)}
                            onMouseEnter={() => setHoveredLeadId(L.id)}
                            onMouseLeave={() => setHoveredLeadId(null)}>
                          <td className="px-2 py-2 w-8" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox"
                              data-testid={`vs-select-${L.id}`}
                              checked={selectedRows.has(L.id)}
                              onChange={() => {
                                const next = new Set(selectedRows);
                                if (next.has(L.id)) next.delete(L.id);
                                else next.add(L.id);
                                setSelectedRows(next);
                              }} />
                          </td>
                          <td className="px-3 py-2 min-w-[200px]">
                            <div className="font-bold text-white truncate flex items-center gap-1.5">
                              {stopN >= 0 && (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#C9A24E]/25 border border-[#C9A24E]/60 text-[10px] font-extrabold text-[#C9A24E] tabular-nums shrink-0"
                                      data-testid={`vs-lead-stop-${L.id}`}
                                      title={t("vs.tbl.stop_title", { n: stopN + 1 })}>
                                  {stopN + 1}
                                </span>
                              )}
                              <span className="truncate">
                                {L.property_name || L.company_name}
                              </span>
                            </div>
                            <div className="text-[10.5px] text-slate-500 truncate">
                              {[L.city, L.state].filter(Boolean).join(", ")}
                            </div>
                          </td>
                          <td className="px-2 py-2 text-slate-300 text-[11px] capitalize">
                            {(L.segment || "").replace(/_/g, " ")}
                          </td>
                          <td className="px-2 py-2">
                            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded"
                                 style={{ background: tstyle.bg, color: tstyle.color }}>
                              <span>{tstyle.icon}</span>
                              <span className="font-extrabold tabular-nums">{L.score}</span>
                              <span className="text-[9.5px]">{tstyle.label}</span>
                            </div>
                          </td>
                          <td className="px-2 py-2 max-w-[220px]">
                            <div className="flex flex-wrap gap-1">
                              {sigs.slice(0, 4).map((s) => (
                                <span key={s}
                                  className="text-[9.5px] px-1.5 py-0.5 rounded border border-white/10 bg-white/5 text-slate-300">
                                  {s}
                                </span>
                              ))}
                              {sigs.length > 4 && (
                                <span className="text-[9.5px] text-slate-500">
                                  +{sigs.length - 4}
                                </span>
                              )}
                              {sigs.length === 0 && (
                                <span className="text-[10px] text-slate-500 italic">{t("vs.tbl.unknown_signals")}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-2 text-slate-200 text-[10.5px]">
                            {L.estimated_contract?.headline_range || "—"}
                          </td>
                          <td className="px-2 py-2">
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white font-bold">
                              {L.lead_status || t("vs.tbl.status_new")}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex items-center gap-1"
                                 onClick={(e) => e.stopPropagation()}>
                              <button type="button"
                                onClick={() => setPitchLeads([L])}
                                data-testid={`vs-lead-pitch-${L.id}`}
                                title="Disparar Pitch por IA"
                                disabled={!(L.decision_maker_email || L.email || L.contact_email)}
                                className="w-7 h-7 rounded flex items-center justify-center bg-purple-500/15 hover:bg-purple-500/30 text-purple-300 disabled:opacity-30 disabled:cursor-not-allowed">
                                <Mail className="w-3 h-3" />
                              </button>
                              <button type="button"
                                onClick={() => { setSelectedLead(L); setShowSources(true); }}
                                data-testid={`vs-lead-sources-${L.id}`}
                                title={t("vs.action.view_sources")}
                                className="w-7 h-7 rounded flex items-center justify-center bg-indigo-500/15 hover:bg-indigo-500/30 text-indigo-300">
                                <Eye className="w-3 h-3" />
                              </button>
                              <button type="button"
                                onClick={() => setSelectedLead(L)}
                                data-testid={`vs-lead-open-${L.id}`}
                                title={t("vs.action.open_details")}
                                className="w-7 h-7 rounded flex items-center justify-center bg-white/10 hover:bg-white/25 text-white">
                                <ChevronRight className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </main>
      </div>

      {selectedLead && !showSources && (
        <LeadDrawer lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onSources={() => setShowSources(true)}
          onOpenPitch={(L) => setPitchLeads([L])}
          authHeader={authHeader}
          onChanged={loadLeads} />
      )}
      {selectedLead && showSources && (
        <SourcesModal lead={selectedLead}
          onClose={() => { setShowSources(false); }} />
      )}
      {showRegModal && (
        <RegulatoryModal alert={regAlert}
                          regulations={regulations}
                          onClose={() => setShowRegModal(false)} />
      )}
      {showKwModal && (
        <KeywordsModal authHeader={authHeader}
                        onClose={() => setShowKwModal(false)} />
      )}
      <ClairePitchModal
        open={pitchLeads.length > 0}
        onClose={() => setPitchLeads([])}
        leads={pitchLeads}
        authHeader={authHeader}
        onSent={() => setSelectedRows(new Set())} />
    </div>
  );
}


function LeadDrawer({ lead, onClose, onSources, onOpenPitch, authHeader, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(lead.lead_status || "New");
  // Iter344 · B4 — Auto-populate DM fields with fallbacks from Claire /
  // CRM enrichment. Any of these paths may hold the freshest contact.
  const _pickDm = (path, ...fallbackFields) => {
    if (path) return path;
    const candidates = [
      lead.top_contact, lead.primary_contact, lead.ai_contact,
      Array.isArray(lead.contacts) ? lead.contacts[0] : null,
      Array.isArray(lead.decision_makers) ? lead.decision_makers[0] : null,
      lead.enrichment?.top_contact,
    ].filter(Boolean);
    for (const c of candidates) {
      for (const f of fallbackFields) if (c[f]) return c[f];
    }
    return "";
  };
  const initialDmName  = _pickDm(lead.decision_maker_name,  "full_name", "name");
  const initialDmTitle = _pickDm(lead.decision_maker_title, "title", "job_title", "role");
  const initialDmEmail = _pickDm(lead.decision_maker_email, "email", "contact_email");
  const autoPopulated = {
    name:  !lead.decision_maker_name  && !!initialDmName,
    title: !lead.decision_maker_title && !!initialDmTitle,
    email: !lead.decision_maker_email && !!initialDmEmail,
  };
  const [dmName, setDmName] = useState(initialDmName);
  const [dmTitle, setDmTitle] = useState(initialDmTitle);
  const [dmEmail, setDmEmail] = useState(initialDmEmail);
  const [notes, setNotes] = useState(lead.notes || "");
  // Iter217 · cross-sell + competitor panels
  const [crossSell, setCrossSell] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [loadingXS, setLoadingXS] = useState(true);
  // Iter219 · AI Smart Recommendation
  const [smartReco, setSmartReco] = useState(lead.smart_recommendation || null);
  const [smartLoading, setSmartLoading] = useState(false);
  const [smartCached, setSmartCached] = useState(!!lead.smart_recommendation);

  const t = TIER_STYLE[lead.tier] || TIER_STYLE.low;

  useEffect(() => {
    (async () => {
      setLoadingXS(true);
      try {
        const [xs, cmp] = await Promise.all([
          axios.get(`${API}/vehicle-security/leads/${lead.id}/cross-sell`, authHeader),
          axios.get(`${API}/vehicle-security/leads/${lead.id}/competitor-comparison`, authHeader),
        ]);
        setCrossSell(xs.data);
        setComparison(cmp.data);
      } catch { /* silent */ }
      setLoadingXS(false);
    })();
    // eslint-disable-next-line
  }, [lead.id]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/vehicle-security/leads/${lead.id}`,
        { lead_status: status, decision_maker_name: dmName || null,
          decision_maker_title: dmTitle || null,
          decision_maker_email: dmEmail || null,
          notes: notes || null },
        authHeader);
      toast.success("Lead atualizado");
      onChanged?.();
      onClose();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const generateSmartReco = async (force = false) => {
    setSmartLoading(true);
    try {
      const r = await axios.post(
        `${API}/vehicle-security/leads/${lead.id}/smart-recommendation${force ? "?force=true" : ""}`,
        {},
        { ...authHeader, timeout: 120000 });
      setSmartReco(r.data?.recommendation || null);
      setSmartCached(!!r.data?.cached);
      if (force) toast.success("Recommendation regenerated");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "AI unavailable — try again");
    } finally { setSmartLoading(false); }
  };

  const reasons = lead.score_reasons || [];

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center lg:justify-end"
         data-testid="vs-lead-drawer" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative w-full lg:w-[560px] max-h-[92vh] overflow-y-auto rounded-t-2xl lg:rounded-none lg:rounded-l-2xl border border-white/10 p-5 space-y-4"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold capitalize">
              {(lead.segment || "").replace(/_/g, " ")}
            </div>
            <h3 className="text-[16px] font-extrabold text-white">
              {lead.property_name || lead.company_name}
            </h3>
            <div className="text-[11.5px] text-slate-400">
              {[lead.address, lead.city, lead.state, lead.zip_code].filter(Boolean).join(", ")}
            </div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="vs-drawer-close"
            className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="rounded-lg p-3" style={{ background: t.bg }}>
          <div className="flex items-center gap-2">
            <span className="text-[22px] font-extrabold tabular-nums" style={{ color: t.color }}>
              {lead.score}
            </span>
            <span className="text-[13px] font-bold" style={{ color: t.color }}>
              {t.icon} {t.label} Opportunity
            </span>
          </div>
          {reasons.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-[11px] text-slate-200">
              {reasons.slice(0, 6).map((r, i) => (
                <li key={i}>· {r}</li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">
            Property signals
          </div>
          <div className="flex flex-wrap gap-1">
            {(lead.signals || []).map((s) => (
              <span key={s}
                className="text-[10.5px] px-1.5 py-0.5 rounded border border-white/10 bg-white/5 text-slate-300">
                {s}
              </span>
            ))}
            {(lead.signals || []).length === 0 && (
              <span className="text-[11px] text-slate-500 italic">No signals detected yet</span>
            )}
          </div>
        </div>

        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">
            Estimated Opportunity
          </div>
          <div className="text-[13px] text-white font-bold">
            {lead.estimated_contract?.headline_range || "—"}
          </div>
          <div className="text-[10.5px] text-slate-500 mt-0.5">
            Range only — final pricing depends on scope + integration + services chosen.
          </div>
        </div>

        {/* Iter219 — AI Smart Recommendation (§ 22) */}
        <SmartRecoPanel
          lead={lead}
          authHeader={authHeader}
          reco={smartReco}
          loading={smartLoading}
          cached={smartCached}
          onGenerate={() => generateSmartReco(false)}
          onRegenerate={() => generateSmartReco(true)}
        />

        {/* Iter223 — VS ↔ Smith prospect link */}
        <SmithLinkPanel lead={lead} authHeader={authHeader} />

        {/* Iter217 — Recommended Package + Cross-Sell Engine (§ 8, § 9) */}
        <div className="rounded-lg border border-purple-500/25 bg-purple-500/5 p-3 space-y-2"
             data-testid="vs-drawer-crosssell">
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-purple-300" />
            <div className="text-[10.5px] uppercase tracking-widest text-purple-200 font-bold">
              Recommended Package
            </div>
          </div>
          {loadingXS && <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-300" />}
          {crossSell?.package && (
            <div>
              <div className="text-[12.5px] font-bold text-white">
                Package {crossSell.package.package} — {crossSell.package.name}
              </div>
              <div className="text-[10.5px] text-slate-400 mt-0.5">
                {crossSell.package.reason}
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5"
                   data-testid="vs-drawer-package-includes">
                {crossSell.package.includes.map((it) => (
                  <span key={it}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-purple-500/40 bg-purple-500/10 text-purple-100">
                    {it}
                  </span>
                ))}
              </div>
            </div>
          )}
          {crossSell?.recommendations?.length > 0 && (
            <div className="pt-2 border-t border-purple-500/15 space-y-1.5">
              <div className="text-[10.5px] uppercase tracking-widest text-purple-200 font-bold">
                Cross-Sell Recommendations ({crossSell.recommendations.length})
              </div>
              {crossSell.recommendations.map((r, i) => (
                <div key={i}
                     data-testid={`vs-drawer-crosssell-rec-${i}`}
                     className="rounded border border-white/10 bg-black/20 p-2">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <div className="text-[11.5px] font-bold text-white">
                      {r.headline}
                    </div>
                    <span className="text-[9.5px] tabular-nums text-purple-200"
                          title="Rule priority">
                      p{r.priority}
                    </span>
                  </div>
                  <div className="text-[10.5px] text-slate-400">{r.reason}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {r.labels.map((lbl, j) => (
                      <span key={j}
                        className={`text-[9.5px] px-1.5 py-0.5 rounded font-bold ${
                          r.kinds[j] === "service"
                            ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/40"
                            : "bg-sky-500/15 text-sky-200 border border-sky-500/40"}`}>
                        + {lbl}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {crossSell?.recommendations?.length === 0 && !loadingXS && (
            <div className="text-[11px] text-slate-500 italic">
              Nenhuma regra automática se aplica — adicione signals via Enrich.
            </div>
          )}
        </div>

        {/* Iter217 — Competitor Intelligence (§ 25) */}
        {comparison && (
          <div className="rounded-lg border border-red-500/25 bg-red-500/5 p-3 space-y-2"
               data-testid="vs-drawer-competitors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5 text-red-300" />
                <div className="text-[10.5px] uppercase tracking-widest text-red-200 font-bold">
                  Competitor Landscape
                </div>
              </div>
              <button type="button"
                onClick={() => setShowComparison(true)}
                data-testid="vs-drawer-comparison-open"
                className="h-6 px-2 rounded text-[10px] font-bold text-white bg-red-600/70 hover:bg-red-600 inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> Full comparison
              </button>
            </div>
            {comparison.incumbents?.length > 0 ? (
              <div className="space-y-1.5">
                <div className="text-[10.5px] text-red-100">
                  Signals suggest this property may already work with:
                </div>
                {comparison.incumbents.map((v) => (
                  <div key={v.id}
                       data-testid={`vs-drawer-incumbent-${v.id}`}
                       className="rounded border border-white/10 bg-black/20 p-2">
                    <div className="text-[11.5px] font-bold text-white">
                      {v.company} <span className="text-slate-400 font-normal">· {v.product}</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Matched signal: <em>{v.matched_on}</em>
                    </div>
                    <div className="text-[10.5px] text-slate-300 mt-1 italic">
                      {v.smith_positioning}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[10.5px] text-slate-400">
                No incumbent vendor detected in this lead's public signals.
                Reference table available in <em>Full comparison</em>.
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Field label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              data-testid="vs-drawer-status"
              className="w-full h-9 px-2 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white">
              {["New","Researching","Qualified","Contacted","Email Sent","Called",
                "Meeting Scheduled","Proposal Sent","Negotiation","Won","Lost","Future Follow-Up"]
                .map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Website">
            {lead.website ? (
              <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`}
                 target="_blank" rel="noreferrer"
                 className="text-[12px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 h-9">
                <ExternalLink className="w-3 h-3" /> Open
              </a>
            ) : <span className="text-[11.5px] text-slate-500 italic h-9 flex items-center">Unknown</span>}
          </Field>
          <Field label={<>Decision maker {autoPopulated.name && <AiBadge />}</>}>
            <input value={dmName} onChange={(e) => setDmName(e.target.value)}
              data-testid="vs-drawer-dm-name"
              placeholder="Name"
              className="w-full h-9 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
          </Field>
          <Field label={<>Title {autoPopulated.title && <AiBadge />}</>}>
            <input value={dmTitle} onChange={(e) => setDmTitle(e.target.value)}
              data-testid="vs-drawer-dm-title"
              placeholder="e.g. Property Manager"
              className="w-full h-9 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
          </Field>
          <Field label={<>Email {autoPopulated.email && <AiBadge />}</>}>
            <input value={dmEmail} onChange={(e) => setDmEmail(e.target.value)}
              data-testid="vs-drawer-dm-email"
              placeholder="name@company.com"
              className="w-full h-9 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
          </Field>
          <Field label="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="vs-drawer-notes" rows={2}
              className="w-full rounded-lg border border-white/15 bg-black/30 p-2 text-[12.5px] text-white" />
          </Field>
        </div>

        <div className="flex gap-2 pt-2 border-t border-white/5">
          <button type="button" onClick={onSources}
            data-testid="vs-drawer-sources-btn"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold text-white hover:border-purple-500/60 inline-flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> View Sources ({(lead.sources || []).length})
          </button>
          <button type="button"
            onClick={() => onOpenPitch?.({ ...lead,
              decision_maker_name: dmName, decision_maker_title: dmTitle,
              decision_maker_email: dmEmail })}
            disabled={!dmEmail}
            data-testid="vs-drawer-pitch-btn"
            title={dmEmail ? "Gera preview do pitch com Claire" : "Preencha o email primeiro"}
            className="h-9 px-3 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
            <Sparkles className="w-3.5 h-3.5" /> Disparar Pitch por IA
          </button>
          <button type="button" onClick={save} disabled={saving}
            data-testid="vs-drawer-save-btn"
            className="h-9 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50 ml-auto"
            style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>
      {showComparison && comparison && (
        <ComparisonModal comparison={comparison}
          onClose={() => setShowComparison(false)} />
      )}
    </div>
  );
}


function ComparisonModal({ comparison, onClose }) {
  const smith = comparison.smith;
  const refs = comparison.neutral_reference || [];
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center"
         data-testid="vs-comparison-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative w-[880px] max-w-[96vw] max-h-[90vh] overflow-y-auto rounded-2xl border border-red-500/40 p-5 space-y-3"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-[14px] font-bold text-white">
              Competitor Comparison — factual reference
            </h3>
            <div className="text-[10.5px] text-slate-400">
              Neutral, publicly-sourced. Never used as attack copy.
            </div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="vs-comparison-close"
            className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
          <div className="text-[11px] uppercase tracking-widest text-emerald-200 font-bold mb-1">
            Smith SecureVehicle™ — differentiators
          </div>
          <ul className="text-[11.5px] text-slate-100 space-y-0.5">
            {(smith.differentiators || []).map((d, i) => (
              <li key={i}>· {d}</li>
            ))}
          </ul>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="vs-comparison-table">
            <thead className="bg-white/5 text-slate-400 uppercase tracking-widest text-[9.5px]">
              <tr>
                <th className="text-left px-2 py-2 font-bold">Vendor</th>
                <th className="text-center px-2 py-2 font-bold">ALPR</th>
                <th className="text-center px-2 py-2 font-bold">CCTV</th>
                <th className="text-center px-2 py-2 font-bold">Access</th>
                <th className="text-center px-2 py-2 font-bold">Local Storage</th>
                <th className="text-left px-2 py-2 font-bold">Retention</th>
                <th className="text-left px-2 py-2 font-bold">Positioning</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              <tr className="bg-emerald-500/5">
                <td className="px-2 py-2 font-bold text-emerald-200">Smith SecureVehicle™</td>
                <td className="px-2 py-2 text-center">✅</td>
                <td className="px-2 py-2 text-center">✅</td>
                <td className="px-2 py-2 text-center">✅</td>
                <td className="px-2 py-2 text-center">✅</td>
                <td className="px-2 py-2 text-emerald-100">{smith.data_retention}</td>
                <td className="px-2 py-2 text-emerald-100">Technology + Services in one contract</td>
              </tr>
              {refs.map((c) => (
                <tr key={c.id}
                    data-testid={`vs-comparison-row-${c.id}`}
                    className="hover:bg-white/5">
                  <td className="px-2 py-2 font-bold text-white">
                    <a href={c.source} target="_blank" rel="noreferrer"
                       className="hover:text-sky-300 inline-flex items-center gap-1">
                      {c.company} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </td>
                  <td className="px-2 py-2 text-center">{c.alpr ? "✅" : "—"}</td>
                  <td className="px-2 py-2 text-center">{c.cctv ? "✅" : "—"}</td>
                  <td className="px-2 py-2 text-center">{c.access_control ? "✅" : "—"}</td>
                  <td className="px-2 py-2 text-center">{c.local_storage ? "✅" : "—"}</td>
                  <td className="px-2 py-2 text-slate-300">{c.data_retention}</td>
                  <td className="px-2 py-2 text-slate-300 max-w-[240px]">
                    <div className="text-[10.5px] italic">{c.smith_positioning}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-[10.5px] text-slate-500 italic border-t border-white/5 pt-2">
          {comparison.disclaimer}
        </div>
      </div>
    </div>
  );
}


function SourcesModal({ lead, onClose }) {
  const sources = lead.sources || [];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center"
         data-testid="vs-sources-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative w-[560px] max-w-[94vw] max-h-[80vh] overflow-y-auto rounded-2xl border border-purple-500/40 p-5 space-y-3"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] font-bold text-white">
            Sources · {lead.property_name || lead.company_name}
          </h3>
          <button type="button" onClick={onClose}
            data-testid="vs-sources-close"
            className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="text-[10.5px] text-slate-400">
          Every claim below is grounded in a public source. Fields marked
          <span className="italic text-slate-500"> Unknown </span>
          were NOT verified — never invented.
        </div>

        {sources.length === 0 && (
          <div className="text-[12px] text-slate-500 italic py-4">
            No sources captured yet — run Discovery or Enrich this lead.
          </div>
        )}
        {sources.map((s, i) => (
          <div key={i} className="rounded-lg border border-white/10 p-3">
            <div className="flex items-center gap-2">
              <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-200 font-bold">
                Confidence {s.confidence || 60}
              </span>
              <a href={s.url} target="_blank" rel="noreferrer"
                 className="text-[12px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 break-all">
                <ExternalLink className="w-3 h-3" /> {s.title || s.url}
              </a>
            </div>
            {s.captured_at && (
              <div className="text-[10px] text-slate-500 mt-1">
                Captured: {new Date(s.captured_at).toLocaleString("en-US")}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold mb-1 inline-flex items-center gap-1">{label}</div>
      {children}
    </div>
  );
}

// Iter344 · B4 — Marks fields whose value came from AI/CRM enrichment.
function AiBadge() {
  return (
    <span data-testid="vs-drawer-ai-badge"
          className="inline-flex items-center gap-0.5 text-[8px] font-black tracking-widest px-1 py-[1px] rounded bg-purple-500/25 text-purple-200 border border-purple-500/40">
      <Sparkles className="w-2 h-2" /> AI
    </span>
  );
}


// ── Iter219 · AI Smart Recommendation Panel ──────────────────────────
// Iter221 adds two action buttons wired to the reco payload:
//   • Print Brief   → opens a print-optimised 1-page window
//   • Outreach Seq. → generates + shows an email + 3 SMS cadence
function SmartRecoPanel({ lead, authHeader, reco, loading, cached, onGenerate, onRegenerate }) {
  const [expanded, setExpanded] = useState({
    why: true, gaps: true, risks: true, solution: true, objections: true,
  });
  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));
  // Iter221 · Outreach sequence modal
  const [showSeqModal, setShowSeqModal] = useState(false);
  const [sequence, setSequence] = useState(lead?.outreach_sequence || null);
  const [seqLoading, setSeqLoading] = useState(false);

  const openOutreachSequence = async (force = false) => {
    setShowSeqModal(true);
    if (sequence && !force) return;
    setSeqLoading(true);
    try {
      const r = await axios.post(
        `${API}/vehicle-security/leads/${lead.id}/outreach-sequence${force ? "?force=true" : ""}`,
        {},
        { ...authHeader, timeout: 120000 });
      setSequence(r.data?.sequence || null);
      if (force) toast.success("Outreach sequence regenerated");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao gerar sequência");
    } finally { setSeqLoading(false); }
  };

  const printBrief = () => printSmartRecoBrief({ lead, reco });

  // Empty state — no reco yet, offer to generate
  if (!reco && !loading) {
    return (
      <div className="rounded-lg border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/10 to-purple-500/5 p-4"
           data-testid="vs-drawer-smartreco-empty">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
               style={{ background: "linear-gradient(135deg,#A855F7,#7C3AED)" }}>
            <Brain className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-[12.5px] font-extrabold text-white">
              AI Smart Recommendation
            </div>
            <div className="text-[10.5px] text-fuchsia-200">
              Claire builds your call brief in 5 sections.
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-300 mb-3 leading-relaxed">
          Get <strong>WHY</strong> this property, <strong>GAPS</strong>, <strong>RISKS</strong>,
          <strong> SOLUTION</strong> and top <strong>OBJECTIONS</strong> with
          ready-to-say responses — grounded on verified signals only.
        </div>
        <button type="button" onClick={onGenerate}
          data-testid="vs-drawer-smartreco-generate"
          className="w-full h-10 rounded-lg text-[12.5px] font-bold text-white inline-flex items-center justify-center gap-1.5"
          style={{ background: "linear-gradient(135deg,#A855F7 0%,#7C3AED 100%)" }}>
          <Wand2 className="w-4 h-4" /> Generate AI Recommendation
        </button>
      </div>
    );
  }

  // Loading skeleton
  if (loading && !reco) {
    return (
      <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-4"
           data-testid="vs-drawer-smartreco-loading">
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-fuchsia-300 animate-spin" />
          <div>
            <div className="text-[12.5px] font-extrabold text-white">
              Claire is thinking…
            </div>
            <div className="text-[10.5px] text-slate-400">
              Analyzing signals, packages and objection patterns · ~20-30s
            </div>
          </div>
        </div>
        <div className="mt-3 space-y-1.5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-3 rounded bg-white/5 animate-pulse" style={{ width: `${100 - i * 12}%` }} />
          ))}
        </div>
      </div>
    );
  }

  if (!reco) return null;

  const sectionColor = {
    why:       { border: "border-emerald-500/30", bg: "bg-emerald-500/8",  chip: "text-emerald-200", icon: Target,        label: "WHY this property" },
    gaps:      { border: "border-sky-500/30",     bg: "bg-sky-500/8",       chip: "text-sky-200",     icon: Layers,        label: "GAPS today" },
    risks:     { border: "border-amber-500/30",   bg: "bg-amber-500/8",     chip: "text-amber-200",   icon: AlertTriangle, label: "RISKS" },
    solution:  { border: "border-fuchsia-500/30", bg: "bg-fuchsia-500/8",   chip: "text-fuchsia-200", icon: Zap,           label: "SOLUTION" },
    objections:{ border: "border-red-500/30",     bg: "bg-red-500/8",       chip: "text-red-200",     icon: MessageSquare, label: "OBJECTIONS" },
  };

  const copyOpening = async () => {
    try {
      await navigator.clipboard.writeText(reco.opening_line || "");
      toast.success("Opening line copied");
    } catch { /* silent */ }
  };

  const Section = ({ id, items, renderItem }) => {
    const cfg = sectionColor[id];
    const Icon = cfg.icon;
    const isOpen = expanded[id];
    return (
      <div className={`rounded-lg border ${cfg.border} ${cfg.bg} overflow-hidden`}
           data-testid={`vs-drawer-smartreco-${id}`}>
        <button type="button" onClick={() => toggle(id)}
          data-testid={`vs-drawer-smartreco-${id}-toggle`}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition-colors">
          <Icon className={`w-3.5 h-3.5 ${cfg.chip}`} />
          <div className={`text-[10.5px] uppercase tracking-widest font-bold ${cfg.chip}`}>
            {cfg.label}
          </div>
          <span className="text-[10px] text-slate-500 ml-1">· {items.length}</span>
          <div className="ml-auto text-slate-400">
            {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </div>
        </button>
        {isOpen && (
          <div className="px-3 pb-3 space-y-1.5">
            {items.length === 0 ? (
              <div className="text-[11px] text-slate-500 italic">No items generated.</div>
            ) : items.map((it, i) => renderItem(it, i))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-500/12 to-purple-500/6 p-3 space-y-3"
         data-testid="vs-drawer-smartreco">
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: "linear-gradient(135deg,#A855F7,#7C3AED)" }}>
          <Brain className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-extrabold text-white flex items-center gap-1.5 flex-wrap">
            AI Smart Recommendation
            {reco.confidence && (
              <span className="text-[9.5px] px-1.5 py-0.5 rounded uppercase font-bold"
                    style={{
                      background: reco.confidence === "high" ? "rgba(16,185,129,0.2)"
                                : reco.confidence === "medium" ? "rgba(245,158,11,0.2)"
                                : "rgba(148,163,184,0.2)",
                      color:      reco.confidence === "high" ? "#6EE7B7"
                                : reco.confidence === "medium" ? "#FCD34D"
                                : "#CBD5E1",
                    }}
                    data-testid="vs-drawer-smartreco-confidence">
                {reco.confidence}
              </span>
            )}
            {cached && (
              <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-white/10 text-slate-300 uppercase font-bold">
                cached
              </span>
            )}
          </div>
          <div className="text-[10.5px] text-fuchsia-200 mt-0.5">
            {reco.generated_by === "fallback"
              ? "Baseline template (AI unavailable)"
              : `Generated by ${reco.generated_by || "Claire"} · grounded on verified signals`}
          </div>
        </div>
        <button type="button" onClick={onRegenerate} disabled={loading}
          data-testid="vs-drawer-smartreco-regen"
          title="Regenerate"
          className="shrink-0 w-7 h-7 rounded border border-white/15 flex items-center justify-center text-slate-300 hover:text-white hover:border-fuchsia-400 disabled:opacity-50">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </button>
      </div>

      {/* Iter221 — Action row: Print + Outreach Sequence */}
      <div className="flex gap-2" data-testid="vs-drawer-smartreco-actions">
        <button type="button" onClick={() => openOutreachSequence(false)}
          data-testid="vs-drawer-smartreco-outreach-btn"
          disabled={loading}
          className="flex-1 h-9 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
          <Send className="w-3.5 h-3.5" /> Outreach Sequence
        </button>
        <button type="button" onClick={printBrief}
          data-testid="vs-drawer-smartreco-print-btn"
          className="flex-1 h-9 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center justify-center gap-1.5 border border-white/15 hover:border-fuchsia-400 transition-colors"
          style={{ background: "rgba(255,255,255,0.03)" }}>
          <Printer className="w-3.5 h-3.5" /> Print Brief
        </button>
      </div>

      {/* Opening line — the money quote */}
      {reco.opening_line && (
        <div className="rounded-lg border border-white/10 bg-black/25 p-2.5"
             data-testid="vs-drawer-smartreco-opening">
          <div className="text-[9.5px] uppercase tracking-widest text-fuchsia-200 font-bold mb-1 flex items-center gap-1">
            <Radio className="w-3 h-3" /> Opening line
            <button type="button" onClick={copyOpening}
              data-testid="vs-drawer-smartreco-opening-copy"
              className="ml-auto text-slate-400 hover:text-white inline-flex items-center gap-1 normal-case tracking-normal font-normal text-[10px]">
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <div className="text-[12px] text-slate-100 italic leading-relaxed">
            "{reco.opening_line}"
          </div>
        </div>
      )}

      {/* Five sections */}
      <Section id="why" items={reco.why || []} renderItem={(t, i) => (
        <div key={i} className="text-[11.5px] text-slate-100 flex gap-1.5">
          <span className="text-emerald-400 shrink-0">·</span>
          <span>{t}</span>
        </div>
      )} />

      <Section id="gaps" items={reco.gaps || []} renderItem={(t, i) => (
        <div key={i} className="text-[11.5px] text-slate-100 flex gap-1.5">
          <span className="text-sky-400 shrink-0">·</span>
          <span>{t}</span>
        </div>
      )} />

      <Section id="risks" items={reco.risks || []} renderItem={(t, i) => (
        <div key={i} className="text-[11.5px] text-slate-100 flex gap-1.5">
          <span className="text-amber-400 shrink-0">·</span>
          <span>{t}</span>
        </div>
      )} />

      {/* Solution is a nested object — render specially */}
      {reco.solution && (
        <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/8"
             data-testid="vs-drawer-smartreco-solution">
          <button type="button" onClick={() => toggle("solution")}
            data-testid="vs-drawer-smartreco-solution-toggle"
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition-colors">
            <Zap className="w-3.5 h-3.5 text-fuchsia-200" />
            <div className="text-[10.5px] uppercase tracking-widest font-bold text-fuchsia-200">
              SOLUTION
            </div>
            <div className="ml-auto text-slate-400">
              {expanded.solution ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </div>
          </button>
          {expanded.solution && (
            <div className="px-3 pb-3 space-y-1.5">
              {reco.solution.package_headline && (
                <div className="text-[12px] font-bold text-white">
                  {reco.solution.package_headline}
                </div>
              )}
              {(reco.solution.components || []).length > 0 && (
                <ul className="space-y-1">
                  {reco.solution.components.map((c, i) => (
                    <li key={i} className="text-[11.5px] text-slate-100 flex gap-1.5">
                      <span className="text-fuchsia-400 shrink-0">·</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              )}
              {reco.solution.sequence && (
                <div className="text-[11px] text-slate-300 italic mt-1 pt-1 border-t border-white/5">
                  Sequence: {reco.solution.sequence}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Objections — special: pill + copy */}
      <Section id="objections" items={reco.objections || []} renderItem={(o, i) => (
        <div key={i}
             data-testid={`vs-drawer-smartreco-objection-${i}`}
             className="rounded border border-white/10 bg-black/20 p-2">
          <div className="text-[11px] font-bold text-red-200 mb-0.5">
            "{o.objection}"
          </div>
          <div className="text-[11px] text-slate-100 leading-snug">
            {o.response}
          </div>
          <button type="button"
            data-testid={`vs-drawer-smartreco-objection-${i}-copy`}
            onClick={async () => {
              try { await navigator.clipboard.writeText(o.response || ""); toast.success("Response copied"); } catch { /* silent */ }
            }}
            className="mt-1 text-[10px] text-slate-400 hover:text-white inline-flex items-center gap-1">
            <Copy className="w-3 h-3" /> Copy response
          </button>
        </div>
      )} />

      <div className="text-[9.5px] text-slate-500 italic pt-1 border-t border-white/5">
        Grounded on verified signals only · every field not captured is treated as Unknown.
      </div>
      {showSeqModal && (
        <OutreachSequenceModal
          lead={lead}
          sequence={sequence}
          loading={seqLoading}
          onRegenerate={() => openOutreachSequence(true)}
          onClose={() => setShowSeqModal(false)}
        />
      )}
    </div>
  );
}


// ── Iter221 · Print Smart Reco Brief ────────────────────────────────
// Renders the 5-section brief in a print-optimised A4 window and auto-
// invokes window.print(). Zero backend dependency — reads from the reco
// prop already in state.
function printSmartRecoBrief({ lead, reco }) {
  if (!reco) { toast.error("Nothing to print — generate the AI reco first"); return; }
  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) { toast.error("Popup blocked — allow pop-ups for this site"); return; }

  const esc = (s) =>
    String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;");

  const li = (arr) => (arr || []).map((x) => `<li>${esc(x)}</li>`).join("");
  const obj = (arr) =>
    (arr || [])
      .map((o) => `<li><strong>"${esc(o.objection)}"</strong><br/><span class="obj-resp">${esc(o.response)}</span></li>`)
      .join("");

  const propertyName = lead?.property_name || lead?.company_name || "Unknown property";
  const address = [lead?.address, lead?.city, lead?.state, lead?.zip_code].filter(Boolean).join(", ");
  const segment = (lead?.segment || "").replaceAll("_", " ");
  const score = lead?.score ?? "—";
  const tier = lead?.tier_label || "";
  const est = lead?.estimated_contract?.headline_range || "—";
  const sol = reco.solution || {};

  const html = `<!doctype html>
<html><head><meta charset="utf-8" />
<title>Smart Reco Brief · ${esc(propertyName)}</title>
<style>
  @page { size: Letter; margin: 0.5in; }
  * { box-sizing: border-box; }
  body { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #0F172A; margin: 0; line-height: 1.35; font-size: 11pt; }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #DC2626; padding-bottom: 10px; margin-bottom: 12px; }
  .brand { display: flex; gap: 10px; align-items: center; }
  .brand-badge { width: 34px; height: 34px; border-radius: 8px; background: linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%); color: white; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 18px; }
  .brand-title { font-size: 13pt; font-weight: 800; }
  .brand-sub { font-size: 9pt; color: #64748B; }
  .score-block { text-align: right; }
  .score-value { font-size: 28pt; font-weight: 800; color: #DC2626; line-height: 1; }
  .score-label { font-size: 9pt; color: #64748B; letter-spacing: 0.05em; text-transform: uppercase; font-weight: 700; }
  .prop-title { font-size: 14pt; font-weight: 800; margin: 0 0 4px; }
  .prop-meta { color: #475569; font-size: 10pt; margin-bottom: 12px; }
  .meta-row { display: flex; gap: 16px; font-size: 9.5pt; color: #64748B; margin-bottom: 12px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
  .section { border: 1px solid #E2E8F0; border-radius: 6px; padding: 8px 10px; page-break-inside: avoid; }
  .section h3 { margin: 0 0 6px; font-size: 9pt; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 800; }
  .section.why h3       { color: #059669; border-bottom: 1px solid #A7F3D0; padding-bottom: 3px; }
  .section.gaps h3      { color: #0284C7; border-bottom: 1px solid #BAE6FD; padding-bottom: 3px; }
  .section.risks h3     { color: #B45309; border-bottom: 1px solid #FDE68A; padding-bottom: 3px; }
  .section.solution h3  { color: #9333EA; border-bottom: 1px solid #E9D5FF; padding-bottom: 3px; }
  .section.objections h3{ color: #DC2626; border-bottom: 1px solid #FECACA; padding-bottom: 3px; }
  .section.objections { grid-column: 1 / -1; }
  ul { margin: 0; padding-left: 18px; }
  li { margin: 2px 0; font-size: 10pt; }
  .opening { background: #F1F5F9; border-left: 3px solid #A855F7; padding: 8px 10px; border-radius: 4px; font-style: italic; margin-bottom: 12px; font-size: 10.5pt; }
  .opening-label { font-size: 8.5pt; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; color: #9333EA; margin-bottom: 3px; font-style: normal; }
  .pkg-headline { font-weight: 700; font-size: 10.5pt; margin: 2px 0 4px; }
  .seq { font-style: italic; color: #475569; font-size: 9.5pt; margin-top: 4px; padding-top: 4px; border-top: 1px dashed #CBD5E1; }
  .obj-resp { color: #334155; font-size: 9.5pt; }
  footer { border-top: 1px solid #E2E8F0; padding-top: 8px; margin-top: 14px; font-size: 8pt; color: #94A3B8; text-align: center; }
  .print-tag { font-size: 8pt; color: #94A3B8; margin-left: auto; }
  @media screen { body { max-width: 780px; margin: 20px auto; padding: 24px; background: #F8FAFC; box-shadow: 0 4px 24px rgba(0,0,0,0.08); } }
  @media print { .no-print { display: none; } }
</style>
</head><body>
  <header>
    <div class="brand">
      <div class="brand-badge">S</div>
      <div>
        <div class="brand-title">Smith SecureVehicle™ · Call Brief</div>
        <div class="brand-sub">Private Vehicle Intelligence &amp; Property Security</div>
      </div>
    </div>
    <div class="score-block">
      <div class="score-value">${esc(score)}</div>
      <div class="score-label">${esc(tier)} · Opportunity</div>
    </div>
  </header>

  <h1 class="prop-title">${esc(propertyName)}</h1>
  <div class="prop-meta">${esc(address || "—")}</div>
  <div class="meta-row">
    <span><strong>Segment</strong>: ${esc(segment) || "—"}</span>
    <span><strong>Est. tech</strong>: ${esc(est)}</span>
    <span><strong>Confidence</strong>: ${esc(reco.confidence || "—")}</span>
    <span class="print-tag">Printed ${new Date().toLocaleString("en-US")}</span>
  </div>

  ${reco.opening_line ? `
  <div class="opening">
    <div class="opening-label">Opening line — say this</div>
    "${esc(reco.opening_line)}"
  </div>` : ""}

  <div class="grid">
    <div class="section why"><h3>Why this property</h3><ul>${li(reco.why)}</ul></div>
    <div class="section gaps"><h3>Gaps today</h3><ul>${li(reco.gaps)}</ul></div>
    <div class="section risks"><h3>Risks</h3><ul>${li(reco.risks)}</ul></div>
    <div class="section solution">
      <h3>Solution</h3>
      ${sol.package_headline ? `<div class="pkg-headline">${esc(sol.package_headline)}</div>` : ""}
      <ul>${li(sol.components)}</ul>
      ${sol.sequence ? `<div class="seq">Sequence: ${esc(sol.sequence)}</div>` : ""}
    </div>
    <div class="section objections"><h3>Top objections + your response</h3><ul>${obj(reco.objections)}</ul></div>
  </div>

  <footer>
    Grounded on verified signals only · every field not captured is treated as Unknown.<br/>
    ALPR deployment, retention and signage must be reviewed per jurisdiction.
  </footer>

  <script>
    window.addEventListener("load", () => setTimeout(() => window.print(), 300));
  </script>
</body></html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
}


// ── Iter221 · Outreach Sequence Modal ─────────────────────────────────
// Iter222 adds a "Send this email now" button that stashes the draft in
// sessionStorage and jumps to /smith/outreach with the right context.
function OutreachSequenceModal({ lead, sequence, loading, onRegenerate, onClose }) {
  const navigate = useNavigate();
  const copyOne = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed"); }
  };

  const copyAllSms = async () => {
    const all = (sequence?.sms_sequence || [])
      .map((m) => `Day ${m.day}: ${m.text}`).join("\n\n");
    await copyOne(all, "All SMS");
  };

  const copyFullEmail = async () => {
    const e = sequence?.email;
    if (!e) return;
    const full = `Subject: ${e.subject}\n\n${e.body_plain}`;
    await copyOne(full, "Full email");
  };

  const sendNow = () => {
    const email = sequence?.email;
    if (!email?.subject || !email?.body_plain) {
      toast.error("Nothing to send — regenerate the sequence first");
      return;
    }
    // Stash the draft — SmithOutreach consumes + clears it on load.
    const payload = {
      subject:    email.subject,
      body_plain: email.body_plain,
      preheader:  email.preheader || "",
      cta_line:   email.cta_line || "",
      vs_lead_id: lead?.id,
      lead_snapshot: {
        property_name: lead?.property_name || lead?.company_name || "",
        company_name:  lead?.company_name || lead?.property_name || "",
        city:          lead?.city || "",
        state:         lead?.state || "",
        segment:       lead?.segment || "",
        website:       lead?.website || "",
        decision_maker_name:  lead?.decision_maker_name  || "",
        decision_maker_title: lead?.decision_maker_title || "",
        decision_maker_email: lead?.decision_maker_email || "",
      },
      generated_at: sequence?.generated_at || new Date().toISOString(),
    };
    try {
      sessionStorage.setItem("vs_outreach_draft", JSON.stringify(payload));
    } catch { /* quota — proceed anyway */ }

    // Prefer the linked smith prospect when present (fast path via Iter220).
    // Otherwise fall back to vs_lead_id which SmithOutreach hydrates from the
    // /vehicle-security/leads/{id} endpoint.
    const params = new URLSearchParams({ tab: "compose", vs_draft: "1" });
    if (lead?.smith_prospect_id) {
      params.set("prospect_id", lead.smith_prospect_id);
    } else if (lead?.decision_maker_email) {
      params.set("email", lead.decision_maker_email);
      params.set("vs_lead_id", lead.id);
    } else {
      params.set("vs_lead_id", lead.id);
    }
    toast.success("Draft handed to Outreach AI — Claire is holding it for you");
    navigate(`/smith/outreach?${params.toString()}`);
  };

  const propertyName = lead?.property_name || lead?.company_name || "";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center"
         data-testid="vs-outreach-sequence-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative w-[720px] max-w-[96vw] max-h-[92vh] overflow-y-auto rounded-2xl border border-sky-500/40 p-5 space-y-3"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5">
              <Send className="w-4 h-4 text-sky-300" />
              <h3 className="text-[14px] font-bold text-white">
                Outreach Sequence — ready to send
              </h3>
            </div>
            <div className="text-[10.5px] text-slate-400 mt-0.5">
              {propertyName} · Email #1 + 3 SMS follow-ups grounded on the Smart Reco.
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button type="button" onClick={onRegenerate} disabled={loading}
              data-testid="vs-outreach-sequence-regen"
              title="Regenerate"
              className="h-8 px-2 rounded border border-white/15 text-[10.5px] font-bold text-slate-100 hover:border-sky-400 inline-flex items-center gap-1 disabled:opacity-50">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Regenerate
            </button>
            <button type="button" onClick={onClose}
              data-testid="vs-outreach-sequence-close"
              className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {loading && !sequence && (
          <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-4"
               data-testid="vs-outreach-sequence-loading">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-sky-300 animate-spin" />
              <div className="text-[12px] text-white font-bold">
                Claire is drafting your cadence…
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-3 rounded bg-white/5 animate-pulse" style={{ width: `${95 - i * 10}%` }} />
              ))}
            </div>
          </div>
        )}

        {sequence && (
          <>
            {/* Email block */}
            <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 space-y-2"
                 data-testid="vs-outreach-sequence-email">
              <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-sky-300" />
                <div className="text-[10.5px] uppercase tracking-widest font-bold text-sky-200">
                  Email #1 · Day 0
                </div>
                <button type="button" onClick={sendNow}
                  data-testid="vs-outreach-sequence-send-now"
                  className="ml-auto h-7 px-2.5 rounded text-[10.5px] font-bold text-white inline-flex items-center gap-1"
                  style={{ background: "linear-gradient(135deg,#10B981 0%,#059669 100%)" }}>
                  <Send className="w-3 h-3" /> Send this email now
                </button>
                <button type="button" onClick={copyFullEmail}
                  data-testid="vs-outreach-sequence-email-copy"
                  className="text-[10px] text-slate-300 hover:text-white inline-flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy full
                </button>
              </div>
              <div>
                <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Subject</div>
                <div className="text-[12.5px] font-bold text-white"
                     data-testid="vs-outreach-sequence-email-subject">
                  {sequence.email?.subject}
                </div>
              </div>
              {sequence.email?.preheader && (
                <div>
                  <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Preheader</div>
                  <div className="text-[11px] text-slate-300 italic">
                    {sequence.email.preheader}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Body</div>
                <pre className="text-[11.5px] text-slate-100 whitespace-pre-wrap font-sans leading-relaxed p-2 rounded bg-black/30 border border-white/5"
                     data-testid="vs-outreach-sequence-email-body">
{sequence.email?.body_plain}
                </pre>
              </div>
              {sequence.email?.cta_line && (
                <div className="text-[10.5px] text-sky-200 italic">
                  ↳ CTA: "{sequence.email.cta_line}"
                </div>
              )}
            </div>

            {/* SMS sequence */}
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2"
                 data-testid="vs-outreach-sequence-sms">
              <div className="flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5 text-emerald-300" />
                <div className="text-[10.5px] uppercase tracking-widest font-bold text-emerald-200">
                  SMS Follow-ups ({sequence.sms_sequence?.length || 0})
                </div>
                <button type="button" onClick={copyAllSms}
                  data-testid="vs-outreach-sequence-sms-copy-all"
                  className="ml-auto text-[10px] text-slate-300 hover:text-white inline-flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy all
                </button>
              </div>
              <div className="space-y-1.5">
                {(sequence.sms_sequence || []).map((m, i) => (
                  <div key={i}
                       data-testid={`vs-outreach-sequence-sms-${i}`}
                       className="rounded border border-white/10 bg-black/20 p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200 font-bold uppercase">
                        Day {m.day}
                      </span>
                      <span className="text-[9.5px] text-slate-500">
                        {m.text.length}/155 chars
                      </span>
                      <button type="button"
                        data-testid={`vs-outreach-sequence-sms-${i}-copy`}
                        onClick={() => copyOne(m.text, `SMS Day ${m.day}`)}
                        className="ml-auto text-[10px] text-slate-400 hover:text-white inline-flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <div className="text-[11.5px] text-slate-100 leading-snug">
                      {m.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Signature + notes */}
            {sequence.signature_line && (
              <div className="text-[10.5px] text-slate-400">
                <strong>Signature</strong>: {sequence.signature_line}
              </div>
            )}
            {sequence.notes?.length > 0 && (
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5">
                <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold mb-1">
                  Rep briefing
                </div>
                <ul className="space-y-0.5">
                  {sequence.notes.map((n, i) => (
                    <li key={i} className="text-[10.5px] text-slate-300">· {n}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-[9.5px] text-slate-500 italic pt-1 border-t border-white/5">
              Grounded on verified signals + cached Smart Reco. Placeholders like
              [First Name] mean the field is Unknown — replace before sending.
            </div>
          </>
        )}
      </div>
    </div>
  );
}




// ── Iter218 · Regulatory Modal (§ 26-27) ─────────────────────────────
function RegulatoryModal({ alert, regulations, onClose }) {
  const impactColor = { high: "#EF4444", medium: "#F59E0B", low: "#0EA5E9" };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center"
         data-testid="vs-regulatory-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative w-[820px] max-w-[96vw] max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 p-5 space-y-3"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5">
              <Scale className="w-4 h-4 text-amber-300" />
              <h3 className="text-[14px] font-bold text-white">
                Regulatory Watch — Florida focus
              </h3>
            </div>
            <div className="text-[10.5px] text-slate-400 mt-0.5">
              Curated tracker. Not legal advice — every row links a public source.
            </div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="vs-regulatory-close"
            className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {alert && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3"
               data-testid="vs-regulatory-modal-alert">
            <div className="text-[11px] uppercase tracking-widest text-red-200 font-bold mb-1">
              Florida — {alert.status?.replace("_", " ")}
            </div>
            <div className="text-[12px] text-slate-100">{alert.message}</div>
            <div className="text-[10px] text-slate-400 mt-1">
              Last reviewed: {alert.last_reviewed}
            </div>
          </div>
        )}

        <div className="space-y-2">
          {regulations.length === 0 && (
            <div className="text-[12px] text-slate-500 italic py-4 text-center">
              Loading regulations…
            </div>
          )}
          {regulations.map((r) => (
            <div key={r.id}
                 data-testid={`vs-regulation-row-${r.id}`}
                 className="rounded-lg border border-white/10 bg-black/20 p-3">
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <span className="text-[9.5px] px-1.5 py-0.5 rounded font-bold"
                      style={{
                        background: `${impactColor[r.impact] || "#0EA5E9"}22`,
                        color: impactColor[r.impact] || "#0EA5E9",
                      }}>
                  {r.impact?.toUpperCase()} IMPACT
                </span>
                <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-white/10 text-slate-200 uppercase font-bold">
                  {r.state} · {r.level}
                </span>
                <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-white/5 text-slate-300 font-bold">
                  {r.status?.replace(/_/g, " ")}
                </span>
                <span className="text-[10px] text-slate-500 ml-auto">
                  {r.effective_date}
                </span>
              </div>
              <div className="text-[12.5px] font-bold text-white">
                {r.topic}
              </div>
              <div className="text-[11px] text-slate-300 mt-1">{r.summary}</div>
              <div className="text-[10.5px] text-emerald-200 mt-1">
                → {r.recommended_action}
              </div>
              <a href={r.source} target="_blank" rel="noreferrer"
                 className="text-[10.5px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 mt-1">
                <ExternalLink className="w-3 h-3" />
                {r.source_title || r.source}
              </a>
            </div>
          ))}
        </div>

        <div className="text-[10.5px] text-slate-500 italic border-t border-white/5 pt-2">
          Smith Protective Services provides Private Vehicle Intelligence with
          customer-controlled retention and access policies — not a public
          surveillance network. Confirm each deployment with local counsel.
        </div>
      </div>
    </div>
  );
}



// ── Iter223 · Smith prospect link panel ──────────────────────────────
// Sits in the lead drawer. Fetches /link-status on mount → shows either
// "Linked to Smith prospect: X" (with unlink option) or a "Try to link"
// button. When Mongo already suggests a match, the button is primed with
// a preview so the rep isn't clicking blind.
function SmithLinkPanel({ lead, authHeader }) {
  const [state, setState] = useState({ loading: true, linked: false, preview: null, meta: null });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(
        `${API}/vehicle-security/leads/${lead.id}/link-status`, authHeader);
      setState({
        loading: false,
        linked:  !!r.data?.linked,
        preview: r.data?.preview || null,
        meta:    r.data?.link_meta || null,
        smith_prospect_id: r.data?.smith_prospect_id || null,
      });
    } catch {
      setState({ loading: false, linked: false, preview: null, meta: null });
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [lead?.id]);

  const doLink = async (force_id = null) => {
    setBusy(true);
    try {
      const url = `${API}/vehicle-security/leads/${lead.id}/link-smith-prospect${force_id ? `?smith_prospect_id=${encodeURIComponent(force_id)}` : ""}`;
      const r = await axios.post(url, {}, authHeader);
      if (r.data?.linked) {
        toast.success(`Vinculado ao Smith prospect via ${r.data.match_reason}`);
        lead.smith_prospect_id = r.data.smith_prospect_id;
      } else {
        toast.info("Nenhum Smith prospect com match para este VS lead");
      }
      await refresh();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao vincular");
    } finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (!window.confirm("Remover vínculo com Smith prospect?")) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/vehicle-security/leads/${lead.id}/link-smith-prospect`,
        authHeader);
      lead.smith_prospect_id = null;
      toast.success("Vínculo removido");
      await refresh();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao desvincular");
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[11px] text-slate-400"
           data-testid="vs-drawer-smithlink-loading">
        <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Checking Smith prospect link…
      </div>
    );
  }

  if (state.linked) {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/8 p-3"
           data-testid="vs-drawer-smithlink-linked">
        <div className="flex items-center gap-1.5 flex-wrap">
          <LinkIcon className="w-3.5 h-3.5 text-emerald-300" />
          <span className="text-[10.5px] uppercase tracking-widest font-bold text-emerald-200">
            Linked to Smith prospect
          </span>
          {state.meta?.match_reason && (
            <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-100 font-bold uppercase">
              via {state.meta.match_reason.replace("_", " ")}
            </span>
          )}
          <button type="button" onClick={doUnlink} disabled={busy}
            data-testid="vs-drawer-smithlink-unlink"
            className="ml-auto h-6 px-2 rounded border border-white/10 text-[10px] text-slate-300 hover:text-red-200 hover:border-red-500/40 inline-flex items-center gap-1 disabled:opacity-50">
            <Unlink className="w-3 h-3" /> Unlink
          </button>
        </div>
        <div className="text-[12px] font-bold text-white mt-1"
             data-testid="vs-drawer-smithlink-name">
          {state.meta?.company_name || "—"}
        </div>
        <div className="text-[10.5px] text-slate-400">
          {[state.meta?.city, state.meta?.state].filter(Boolean).join(", ") || "—"}
          {state.meta?.linked_at && ` · linked ${new Date(state.meta.linked_at).toLocaleDateString("en-US")}`}
        </div>
        <div className="text-[10.5px] text-emerald-200 mt-1.5">
          ↳ "Send this email now" agora usa o prospect_id real (não o fallback vs_lead_id).
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-500/30 bg-white/[0.02] p-3"
         data-testid="vs-drawer-smithlink-unlinked">
      <div className="flex items-center gap-1.5">
        <Link2Off className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-[10.5px] uppercase tracking-widest font-bold text-slate-300">
          Smith prospect link
        </span>
        <span className="text-[10px] text-slate-500 ml-auto">
          Not linked yet
        </span>
      </div>
      {state.preview ? (
        <>
          <div className="mt-2 rounded border border-sky-500/30 bg-sky-500/8 p-2"
               data-testid="vs-drawer-smithlink-preview">
            <div className="text-[9.5px] uppercase tracking-widest font-bold text-sky-200 mb-0.5">
              Suggested match via {state.preview.match_reason.replace("_", " ")}
            </div>
            <div className="text-[12px] font-bold text-white">
              {state.preview.company_name}
            </div>
            <div className="text-[10.5px] text-slate-400">
              {[state.preview.city, state.preview.state].filter(Boolean).join(", ")}
              {state.preview.website && (
                <> · <a href={state.preview.website.startsWith("http") ? state.preview.website : `https://${state.preview.website}`}
                       target="_blank" rel="noreferrer"
                       className="text-sky-300 hover:text-sky-100">
                  {state.preview.website}
                </a></>
              )}
            </div>
          </div>
          <button type="button" onClick={() => doLink()} disabled={busy}
            data-testid="vs-drawer-smithlink-confirm"
            className="mt-2 w-full h-9 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#10B981 0%,#059669 100%)" }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LinkIcon className="w-3.5 h-3.5" />}
            Confirm link
          </button>
        </>
      ) : (
        <>
          <div className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">
            Auto-match will look for a Smith prospect by website → address → company + city.
          </div>
          <button type="button" onClick={() => doLink()} disabled={busy}
            data-testid="vs-drawer-smithlink-try"
            className="mt-2 w-full h-9 rounded-lg text-[11.5px] font-bold text-slate-100 border border-white/15 hover:border-sky-400 inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Try auto-link
          </button>
        </>
      )}
    </div>
  );
}


// ── Iter223 · Google Ads Keywords Modal ──────────────────────────────
function KeywordsModal({ authHeader, onClose }) {
  const [segments, setSegments] = useState([]);
  const [active, setActive] = useState("apartment_community");
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/vehicle-security/keywords`, authHeader);
        setSegments(r.data?.segments || []);
      } catch { /* silent */ }
    })();
  }, [authHeader]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/vehicle-security/keywords?segment=${active}`, authHeader);
        setBundle(r.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [active, authHeader]);

  const downloadCsv = (all = false) => {
    const url = `${API}/vehicle-security/keywords.csv${all ? "" : `?segment=${active}`}`;
    (async () => {
      try {
        const r = await axios.get(url, { ...authHeader, responseType: "blob" });
        const blob = new Blob([r.data], { type: "text/csv" });
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = all
          ? "smith-securevehicle-all-segments-keywords.csv"
          : `smith-securevehicle-${active}-keywords.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(href);
        toast.success("CSV baixado — pronto para importar no Google Ads Editor");
      } catch {
        toast.error("Falha no download do CSV");
      }
    })();
  };

  const copyAllKeywords = async () => {
    if (!bundle?.keywords) return;
    const lines = bundle.keywords.map((k) => k.keyword).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success(`${bundle.keywords.length} keywords copied`);
    } catch { toast.error("Copy failed"); }
  };

  const copyAllNegatives = async () => {
    if (!bundle?.negative_keywords) return;
    const lines = bundle.negative_keywords.map((k) => k.keyword).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success(`${bundle.negative_keywords.length} negatives copied`);
    } catch { toast.error("Copy failed"); }
  };

  const intentColor = {
    commercial: { bg: "rgba(16,185,129,0.18)", fg: "#6EE7B7" },
    problem:    { bg: "rgba(239,68,68,0.18)",  fg: "#FCA5A5" },
    info:       { bg: "rgba(14,165,233,0.18)", fg: "#7DD3FC" },
    brand:      { bg: "rgba(168,85,247,0.18)", fg: "#D8B4FE" },
    competitor: { bg: "rgba(245,158,11,0.18)", fg: "#FCD34D" },
  };
  const matchColor = {
    exact:  { bg: "rgba(220,38,38,0.15)",  fg: "#FCA5A5" },
    phrase: { bg: "rgba(14,165,233,0.15)", fg: "#7DD3FC" },
    broad:  { bg: "rgba(148,163,184,0.15)", fg: "#CBD5E1" },
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center"
         data-testid="vs-keywords-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative w-[900px] max-w-[96vw] max-h-[92vh] overflow-y-auto rounded-2xl border border-sky-500/40 p-5 space-y-3"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5">
              <Wand2 className="w-4 h-4 text-sky-300" />
              <h3 className="text-[14px] font-bold text-white">
                Google Ads · Keyword + Negative Packs
              </h3>
            </div>
            <div className="text-[10.5px] text-slate-400 mt-0.5">
              Curated packs per Smith SecureVehicle™ segment. Validate volume/CPC
              in Keyword Planner before campaign launch.
            </div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="vs-keywords-close"
            className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5"
             data-testid="vs-keywords-tabs">
          {segments.map((s) => (
            <button type="button" key={s.id}
              data-testid={`vs-keywords-tab-${s.id}`}
              onClick={() => setActive(s.id)}
              className={`h-8 px-2.5 rounded text-[10.5px] font-bold border transition-colors ${
                active === s.id
                  ? "border-sky-400 bg-sky-500/20 text-white"
                  : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-sky-500/40"}`}>
              {s.label}
              <span className="text-slate-500 ml-1">· {s.count}</span>
            </button>
          ))}
        </div>

        {loading || !bundle ? (
          <div className="rounded-lg border border-white/10 p-6 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline" />
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3 flex-wrap">
              <div>
                <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold">
                  Segment
                </div>
                <div className="text-[13px] font-bold text-white">
                  {bundle.segment_label}
                </div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <MiniStat label="Keywords"  value={bundle.totals.keywords} />
              <MiniStat label="Negatives" value={bundle.totals.negative_keywords} />
              <MiniStat label="Ad groups" value={bundle.totals.ad_groups} />
              <div className="w-px h-8 bg-white/10" />
              <div className="text-[10.5px] text-slate-300">
                <span className="uppercase tracking-widest text-slate-500 font-bold block">Budget hint</span>
                {bundle.budget_hint}
              </div>

              <div className="ml-auto flex gap-1.5">
                <button type="button" onClick={() => downloadCsv(false)}
                  data-testid="vs-keywords-csv-segment"
                  className="h-9 px-3 rounded text-[11px] font-bold text-white inline-flex items-center gap-1.5"
                  style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
                  <Download className="w-3.5 h-3.5" /> CSV (this segment)
                </button>
                <button type="button" onClick={() => downloadCsv(true)}
                  data-testid="vs-keywords-csv-all"
                  className="h-9 px-3 rounded text-[11px] font-bold text-slate-100 border border-white/15 hover:border-sky-400 inline-flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5" /> All segments
                </button>
              </div>
            </div>

            {bundle.ad_groups?.length > 0 && (
              <div className="rounded-lg border border-white/10 p-3"
                   data-testid="vs-keywords-adgroups">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-2">
                  Ad groups (import-ready)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {bundle.ad_groups.map((g, i) => (
                    <div key={g.name}
                         data-testid={`vs-keywords-adgroup-${i}`}
                         className="rounded border border-white/10 bg-black/20 p-2.5">
                      <div className="text-[11px] font-bold text-white">{g.name}</div>
                      <div className="text-[9.5px] text-slate-500 mt-0.5">
                        {g.keywords.length} keywords
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-lg border border-white/10">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-300 font-bold">
                  Keywords ({bundle.totals.keywords})
                </div>
                <button type="button" onClick={copyAllKeywords}
                  data-testid="vs-keywords-copy-all"
                  className="ml-auto text-[10px] text-slate-300 hover:text-white inline-flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy all
                </button>
              </div>
              <div className="max-h-[300px] overflow-y-auto" data-testid="vs-keywords-list">
                <table className="w-full text-[11px]">
                  <thead className="text-[9.5px] uppercase tracking-widest text-slate-500 sticky top-0 bg-[#0F172A]">
                    <tr>
                      <th className="text-left px-3 py-1.5 font-bold">Keyword</th>
                      <th className="text-left px-3 py-1.5 font-bold">Match</th>
                      <th className="text-left px-3 py-1.5 font-bold">Intent</th>
                      <th className="text-left px-3 py-1.5 font-bold">Persona</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bundle.keywords.map((k, i) => {
                      const ic = intentColor[k.intent] || intentColor.info;
                      const mc = matchColor[k.match_type] || matchColor.phrase;
                      return (
                        <tr key={i}
                            data-testid={`vs-keywords-row-${i}`}
                            className="border-t border-white/5 hover:bg-white/[0.03]">
                          <td className="px-3 py-1.5 text-slate-100">{k.keyword}</td>
                          <td className="px-3 py-1.5">
                            <span className="text-[9.5px] px-1.5 py-0.5 rounded font-bold uppercase"
                                  style={{ background: mc.bg, color: mc.fg }}>
                              {k.match_type}
                            </span>
                          </td>
                          <td className="px-3 py-1.5">
                            <span className="text-[9.5px] px-1.5 py-0.5 rounded font-bold uppercase"
                                  style={{ background: ic.bg, color: ic.fg }}>
                              {k.intent}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-slate-400">{k.persona}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-lg border border-red-500/25 bg-red-500/5">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
                <ShieldAlert className="w-3.5 h-3.5 text-red-300" />
                <div className="text-[10.5px] uppercase tracking-widest text-red-200 font-bold">
                  Negative keywords · protect your ad budget ({bundle.totals.negative_keywords})
                </div>
                <button type="button" onClick={copyAllNegatives}
                  data-testid="vs-keywords-copy-neg-all"
                  className="ml-auto text-[10px] text-slate-300 hover:text-white inline-flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy all
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 p-2 max-h-[220px] overflow-y-auto"
                   data-testid="vs-keywords-negatives">
                {bundle.negative_keywords.map((n, i) => (
                  <div key={i}
                       data-testid={`vs-keywords-neg-${i}`}
                       className="rounded border border-white/5 bg-black/20 p-2">
                    <div className="text-[11px] font-bold text-red-100">− {n.keyword}</div>
                    <div className="text-[10px] text-slate-400 leading-tight mt-0.5">{n.reason}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-[9.5px] text-slate-500 italic pt-1 border-t border-white/5">
              Curated packs. CSV is ready to import into Google Ads Editor as
              "Paused" — marketing enables after final scrubbing.
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-[16px] font-extrabold text-white tabular-nums">{value}</div>
    </div>
  );
}
